import flatpickr from "flatpickr";
import { German } from "flatpickr/dist/l10n/de.js";
import 'select2';

const t = (key, fallback = '') => (window.BL_I18N && BL_I18N[key]) || fallback || key;
const CURRENT_LOCALE = (window.BL_I18N && BL_I18N.locale) || document.documentElement.lang || 'en-US';

const IS_DE = String(CURRENT_LOCALE).toLowerCase().startsWith('de');
const FP_LOCALE = IS_DE ? German : undefined;

const FIELD_MAP_DE = {
    shortDescription: 'shortDescriptionLang2'
};

function getLocalizedPath(path) {
    return (IS_DE && FIELD_MAP_DE[path]) ? FIELD_MAP_DE[path] : path;
}

function tr(key, def, vars = {}) {
    let s = t(key, def);
    return Object.keys(vars).reduce((out, k) => {
        const re = new RegExp('\\{' + k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\}', 'g');
        return out.replace(re, vars[k]);
    }, s);
}

// Selectors
const SELECTORS = {
    ALL_INPUTS: '.ninja-forms-field:not(.select2-hidden-accessible)',
    FORM: '.js-pricing-form',
    MOBILE_FORM: '.js-pricing-form-mobile',
    ERROR_CONTAINER: '.js-breakdown-wrapper'
};

const fieldMapping = {
    'meteringPoint': 'marketLocation',
};

class SingleApartmentApi {
    constructor() {
        this.proxyUrl = '/wp-content/themes/basellodging-theme/api-proxi.php';
        this.loading = true;
        this.apartmentData = {};
        this.addedExtraServices = {};
        this.dates = { checkIn: null, checkOut: null };
        this.guests = 1;
        this.longTermThreshold = 30;
        this.reshPricingBox = {};
        this.apartmentId = apartment.unitId ?? '';
        this.pageId = apartment.pageId ?? '';
        this.buildingId = null;
        this.unitTypeType = null;

        this.lang = (
            (window.ICL_LANGUAGE_CODE) ||
            (window.BL_AJAX && BL_AJAX.lang) ||
            (window.BL_I18N && BL_I18N.locale) ||
            document.documentElement.lang || 'en'
        ).toString().toLowerCase().split(/[-_]/)[0];

        this._availKey = null;      // 'YYYY-MM-DD|YYYY-MM-DD'
        this._availFetch = null;    // in-flight promise

        this._ciPicker = null; // flatpickr instance (check-in)
        this._coPicker = null; // flatpickr instance (check-out)

        this._rvKey   = 'bl_recently_viewed_posts';
        this._rvLimit = 4;
        this._rvTrack(this.apartmentId);
        this._currentStayCategory = null;

        // Extras assets (array of { slug, icon, logo })
        this.extrasAssets = (window.BL_AJAX && (
            BL_AJAX.extras_icons_assets ||
            BL_AJAX.extras_icons_items  ||
            BL_AJAX.extras_assets
        )) || [];

        // Normalize to array
        if (!Array.isArray(this.extrasAssets)) this.extrasAssets = [];

        // Fast lookups
        this._extrasIconBySlug = Object.create(null);
        this._extrasLogoBySlug = Object.create(null);
        this._reindexExtrasAssets();

        this._missingExtrasSlugs = new Set();
        this._extrasIconsSyncing = false;

        this._similarAvailability = null; // { [unitId]: true|false }
        this._similarAvailKey = null;     // 'dateFrom|dateTo|guests'

        this._hiddenExtrasSlugsSet = null;

        this.unsureCheckoutParam = 'unsureCheckout';
        this.unsureCheckoutActive = this.hasUnsureCheckoutParam();

        this.getApartmentData();
    }

    // ===== Recently Viewed helpers =====
    _rvRead() {
        try {
            const raw = localStorage.getItem(this._rvKey);
            const arr = raw ? JSON.parse(raw) : [];
            return Array.isArray(arr) ? arr : [];
        } catch (_) { return []; }
    }

    _rvWrite(list) {
        try { localStorage.setItem(this._rvKey, JSON.stringify(list)); } catch(_) {}
    }

    /**
     * Додає id поста у список недубльовано, останній переглянутий — перший.
     * Ліміт — 4 елементи, без експайру.
     */
    _rvTrack(id) {
        if (!id && id !== 0) return;
        const val = String(id);
        if (!val) return;

        const list = this._rvRead().filter(x => String(x) !== val);
        list.unshift(val);
        this._rvWrite(list.slice(0, this._rvLimit));
    }

    _prioritizeFeatures(list = []) {
        const arr = Array.isArray(list) ? [...list] : [];
        const isUSP = (name) => /^\s*USP\b/i.test(String(name || ''));
        return arr.sort((a, b) => {
            const au = isUSP(a.name), bu = isUSP(b.name);
            if (au !== bu) return au ? -1 : 1;
            return String(a.name || '').localeCompare(String(b.name || ''));
        });
    }

    /** Знадобиться для рендеру секції пізніше */
    getRecentlyViewedIds() {
        return this._rvRead();
    }

    getExtraSlugFromService(svc) {
        // Prefer stable API-provided slug if exists
        if (svc && svc.slug) return this.toFeatureId(svc.slug);

        // Use original (non-localized) name to stay consistent across languages
        const name = String((svc && (svc.name || svc.webName || svc.title)) || '').trim();
        return this.toFeatureId(name);
    }

    _reindexExtrasAssets() {
        // English comments as requested
        this._extrasIconBySlug = Object.create(null);
        this._extrasLogoBySlug = Object.create(null);

        const arr = Array.isArray(this.extrasAssets) ? this.extrasAssets : [];
        for (let i = 0; i < arr.length; i++) {
            const it = arr[i] || {};
            const slug = String(it.slug || '').trim();
            if (!slug) continue;

            const icon = String(it.icon || '').trim();
            const logo = String(it.logo || '').trim();

            if (icon) this._extrasIconBySlug[slug] = icon;
            if (logo) this._extrasLogoBySlug[slug] = logo;
        }
    }

    _getExtraAssetItem(slug) {
        const s = String(slug || '').trim();
        if (!s) return null;
        const arr = Array.isArray(this.extrasAssets) ? this.extrasAssets : [];
        for (let i = 0; i < arr.length; i++) {
            const it = arr[i] || {};
            if (String(it.slug || '').trim() === s) return it;
        }
        return null;
    }

    getExtraIconUrlBySlug(slug) {
        const s = String(slug || '').trim();
        if (s && this._extrasIconBySlug && this._extrasIconBySlug[s]) return this._extrasIconBySlug[s];

        // fallback to default
        if (this._extrasIconBySlug && this._extrasIconBySlug['default']) return this._extrasIconBySlug['default'];
        return '';
    }

    getExtraLogoUrlBySlug(slug) {
        const s = String(slug || '').trim();
        if (s && this._extrasLogoBySlug && this._extrasLogoBySlug[s]) return this._extrasLogoBySlug[s];

        // fallback to default (optional)
        if (this._extrasLogoBySlug && this._extrasLogoBySlug['default']) return this._extrasLogoBySlug['default'];
        return '';
    }

    queueMissingExtraSlug(slug) {
        const s = String(slug || '').trim();
        if (!s) return;

        // If we already have icon or logo for this slug — nothing to sync
        const hasIcon = !!(this._extrasIconBySlug && this._extrasIconBySlug[s]);
        const hasLogo = !!(this._extrasLogoBySlug && this._extrasLogoBySlug[s]);
        if (hasIcon || hasLogo) return;

        this._missingExtrasSlugs.add(s);
    }

    _mergeExtrasAssets(incoming) {
        // Accept array of items OR object map, be tolerant
        let items = [];

        if (Array.isArray(incoming)) {
            items = incoming;
        } else if (incoming && typeof incoming === 'object') {
            // If backend returned { slug: {icon,logo} } or { slug: "iconUrl" }
            items = Object.keys(incoming).map((slug) => {
                const v = incoming[slug];
                if (v && typeof v === 'object') return { slug, icon: v.icon || '', logo: v.logo || '' };
                return { slug, icon: v || '', logo: '' };
            });
        }

        if (!items.length) return;

        // Merge into this.extrasAssets by slug
        const bySlug = Object.create(null);
        (this.extrasAssets || []).forEach((it) => {
            const s = String(it?.slug || '').trim();
            if (!s) return;
            bySlug[s] = { ...it };
        });

        items.forEach((it) => {
            const s = String(it?.slug || '').trim();
            if (!s) return;

            const prev = bySlug[s] || { slug: s };
            bySlug[s] = {
                slug: s,
                icon: (it.icon != null && String(it.icon).trim() !== '') ? it.icon : (prev.icon || ''),
                logo: (it.logo != null && String(it.logo).trim() !== '') ? it.logo : (prev.logo || ''),
            };
        });

        this.extrasAssets = Object.values(bySlug);
        this._reindexExtrasAssets();
    }


    flushMissingExtrasIcons() {
        if (this._extrasIconsSyncing) return;

        const slugs = Array.from(this._missingExtrasSlugs);
        if (!slugs.length) return;

        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && (BL_AJAX.extras_icons_nonce || BL_AJAX.nonce)) || '';

        this._missingExtrasSlugs.clear();
        this._extrasIconsSyncing = true;

        $.ajax({
            url: ajaxUrl,
            method: 'POST',
            dataType: 'json',
            data: {
                action: 'bl_sync_extras_icons',
                _ajax_nonce: nonce,
                slugs: slugs
            }
        })
            .done((res) => {
                // Expect: res.data.items = [{slug,icon,logo}, ...]
                const payload = res?.data || {};
                const incoming = payload.items || payload.assets || payload.map || payload;
                this._mergeExtrasAssets(incoming);
            })
            .always(() => {
                this._extrasIconsSyncing = false;
            });
    }

    getMicroserviceBaseline() {
        const sp = new URLSearchParams(window.location.search);
        const forceDaily = sp.get('stayType') === 'DAILY';

        const pricing = this.apartmentData?.pricing;
        const r = Array.isArray(pricing)
            ? (forceDaily ? (pricing[1] || pricing[0]) : pricing[0])
            : (pricing || {});

        const type = String(r.rateType || '').toUpperCase() === 'MONTHLY' ? 'MONTHLY' : 'DAILY';

        // take the minimum minStay across all pricing items (fallback to r.minStay -> 1)
        const minStayDays = (() => {
            const list = Array.isArray(this.apartmentData?.pricing) ? this.apartmentData.pricing : [];
            const vals = list.map(it => Number(it?.minStay)).filter(n => Number.isFinite(n) && n > 0);
            return vals.length ? Math.min(...vals) : (Number(r.minStay) || 1);
        })();

        return {
            priceFromGrossNet : Number(r.amount) || 0, // unchanged
            priceFromGross    : Number(r.amount) || 0, // unchanged
            priceFromType     : type,                  // unchanged
            minStayDays,                               // ← тільки це взято як мінімум
            totalsGross       : 0                      // unchanged
        };
    }

    getCurrentStayCategoryFromType(rateType) {
        const t = String(rateType || '').toUpperCase();
        return (t === 'MONTHLY' || t === 'PER_MONTH') ? 'long' : 'short';
    }

    getCurrentStayCategory() {
        const base = this.apartmentDataFromResh ? this.extractReshPricing() : this.getMicroserviceBaseline();
        return this.getCurrentStayCategoryFromType(base.priceFromType);
    }

    getAllExtrasList() {
        return Array.isArray(this.apartmentData?.extras) ? this.apartmentData.extras
            : Array.isArray(this.apartmentData?.extraServices) ? this.apartmentData.extraServices
                : Array.isArray(this.apartmentDataFromResh?.extras) ? this.apartmentDataFromResh.extras
                    : [];
    }

    filterExtrasByCategory(list, category) {
        const cat = String(category || '').toLowerCase(); // 'short' | 'long'
        return (Array.isArray(list) ? list : []).filter(s => {
            if (!s || s.includedInRate) return false;
            const w = String(s.webCategoryName || s.webCategory || '').toLowerCase();
            return !w || w === cat;
        });
    }

    /** remove selected extras that are not valid for the current category */
    pruneSelectedExtras(validList) {
        const validIds = new Set((validList || []).map(s => String(s.externalId ?? s.id ?? s.extraId ?? '').trim()).filter(Boolean));
        let changed = false;
        Object.keys(this.addedExtraServices).forEach(k => {
            if (!validIds.has(k)) { delete this.addedExtraServices[k]; changed = true; }
        });
        if (changed) {
            const ids = Object.keys(this.addedExtraServices).sort();
            this.updateUrlParams({ extras: ids.length ? ids.join(',') : null });
        }
    }

    /** re-render extras section for a specific category */
    rerenderExtrasForCategory(category) {
        const all = this.getAllExtrasList();
        const filtered = this.filterExtrasByCategory(all, category);
        this.pruneSelectedExtras(filtered);
        const $root = $('#apartment-root');
        const $container = $root.find('[data-repeat="extras"]');
        this.buildExtraServices($container, filtered, $root);
    }

    appendParam(url, key, val) {
        try {
            const u = new URL(url, window.location.origin);
            u.searchParams.set(key, val);
            return u.toString();
        } catch (e) {
            const sep = url.includes('?') ? '&' : '?';
            return url + sep + encodeURIComponent(key) + '=' + encodeURIComponent(val);
        }
    }

    createCheckoutToken(payload) {
        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && BL_AJAX.nonce) || '';

        const ra = this.apartmentDataFromResh?.rateAvailabilities?.[0]
            || this.apartmentDataFromResh?.rateAvailabilities
            || {};

        const action    = ra.rateType === 'MONTHLY'? 'bl_create_checkout_long_token' : 'bl_create_checkout_token';

        return $.ajax({
            url: ajaxUrl,
            method: 'POST',
            dataType: 'json',
            data: {
                action: action,
                _ajax_nonce: nonce,
                payload: JSON.stringify(payload)
            }
        });
    }

    setCtaLoading($scope, isLoading, labelWhileLoading = 'Checking...') {
        const $wrap = $scope.find('.js-cta');
        const $btn  = $wrap.find('button');
        if (!$btn.length) return;

        if (isLoading) {
            // remember the current label on the container (survives rerenders)
            if (!$wrap.data('ctaOriginalText')) {
                $wrap.data('ctaOriginalText', $.trim($btn.text()) || '');
            }
            $btn.attr('data-loading', '1')
                .prop('disabled', true)
                .addClass('opacity-70 cursor-not-allowed')
                .html('<span class="inline-flex items-center gap-2"><span>'
                    + this.escapeHtml(labelWhileLoading) + '</span></span>');
        } else {
            // restore text only if this is the same button with loader
            if ($btn.is('[data-loading="1"]')) {
                const original = $wrap.data('ctaOriginalText');
                if (typeof original === 'string' && original) $btn.text(original);
                $btn.removeAttr('data-loading');
            }
            $btn.prop('disabled', false).removeClass('opacity-70 cursor-not-allowed');
            // do not clear $wrap.data('ctaOriginalText') on purpose — keep it for next checks
        }
    }

    getSimilarSectionAnchor() {
        // preferred: section wrapper if you have it
        const $sec = $('[data-section="similar-apartments"], #similar-apartments, .js-similar-apartments-section').first();
        if ($sec.length) return $sec;

        // fallback: container where cards are rendered
        return $('[data-repeat="similarUnits.units"]').first();
    }

    scrollToSimilarSection() {
        const $target = this.getSimilarSectionAnchor();
        if (!$target || !$target.length) return;

        const top = $target.offset().top - 20; // small offset for sticky headers if any
        $('html, body').stop(true).animate({ scrollTop: top }, 450);
    }

    buildSimilarOtherBuildingsUrl() {
        const base = (window.BL_AJAX && BL_AJAX.overview_page) ? String(BL_AJAX.overview_page) : '';
        if (!base) return '';

        // Dates: prefer internal state (already synced) -> fallback to URL params
        const dateFrom = (this.dates && this.dates.checkIn) ? this.dates.checkIn : (this.getUrlParamDate('dateFrom') || '');
        const dateTo   = (this.dates && this.dates.checkOut) ? this.dates.checkOut : (this.getUrlParamDate('dateTo') || '');

        // Guests: prefer internal state -> fallback to URL
        const guests = Number(this.guests) || this.getUrlParamInt('guests') || this.getUrlParamInt('maxGuests') || 1;

        // Unit type id: as you requested - from this.apartmentData.unitTypeId
        const unitTypeId =
            this.apartmentData?.unitTypeId ??
            this.apartmentData?.unitType?.id ??
            null;

        try {
            const u = new URL(base, window.location.origin);

            if (dateFrom) u.searchParams.set('dateFrom', dateFrom);
            if (dateTo)   u.searchParams.set('dateTo', dateTo);

            if (guests) u.searchParams.set('guests', String(guests));

            // Keep both keys to be safe (overview can pick the one it supports)
            if (unitTypeId) {
                u.searchParams.set('unitType',   String(unitTypeId));
            }

            // Preserve stayType if currently forced
            try {
                const cur = new URLSearchParams(window.location.search);
                if (cur.get('stayType') === 'DAILY') u.searchParams.set('stayType', 'DAILY');
            } catch (_) {}

            return u.toString();
        } catch (e) {
            // fallback without URL()
            let url = base;
            url = this.appendParam(url, 'dateFrom', dateFrom || '');
            url = this.appendParam(url, 'dateTo',   dateTo || '');
            url = this.appendParam(url, 'guests',   String(guests));
            if (unitTypeId) url = this.appendParam(url, 'unitTypeId', String(unitTypeId));
            return url;
        }
    }

    renderSimilarAnchor($scope) {
        const $break = $scope && $scope.jquery ? $scope : $($scope || document);
        if (!$break.length) return;

        // prevent duplicates
        $break.find('.js-similar-anchor, .js-similar-alt').remove();

        // Bind scroll handler once
        if (!this._similarAnchorBound) {
            $(document).off('click.blSimilarAnchor')
                .on('click.blSimilarAnchor', '.js-similar-anchor', (e) => {
                    e.preventDefault();
                    this.scrollToSimilarSection();
                });
            this._similarAnchorBound = true;
        }

        const labelAnchor = t('see_available_in_building', 'See available apartments in this building');

        // Determine if we have ANY available similar apartment in the same building for current request
        const map = this._similarAvailability;
        const hasAnyAvailableSimilar = !!(map && typeof map === 'object' && Object.values(map).some(v => v === true));

        // If none are available -> show alternative message + link to overview
        if (!hasAnyAvailableSimilar) {
            const msg = t('no_similar_in_building', 'No similar apartments in that building available');
            const linkLabel = t('see_similar_other_buildings', 'See similar apartments in other buildings');

            const url = this.buildSimilarOtherBuildingsUrl();

            const html = `
            <div class="mt-2 js-similar-alt">
                <div class="text-sm text-gray-700">${this.escapeHtml(msg)}</div>
                ${url ? `
                    <div class="mt-2">
                        <a href="${this.escapeHtml(url)}"
                           target="_blank"
                           rel="noopener noreferrer"
                           class="text-sm underline text-basel-blue hover:text-basel-blue-dark">
                            ${this.escapeHtml(linkLabel)}
                        </a>
                    </div>
                ` : ``}
            </div>
        `;

            $break.append(html);
            return;
        }

        // Otherwise keep current behavior: anchor to "similar in this building"
        const html = `
        <div class="mt-2">
            <a href="#"
               class="js-similar-anchor text-sm underline text-basel-blue hover:text-basel-blue-dark">
                ${this.escapeHtml(labelAnchor)}
            </a>
        </div>
    `;
        $break.append(html);
    }

    updateSimilarAvailabilityUI(reqKey) {
        // If no key -> we cannot safely apply any availability state, reset to defaults
        if (!reqKey) {
            this.resetSimilarAvailabilityUI();
            return;
        }

        // Protect against race conditions
        if (!this._similarAvailability || this._similarAvailKey !== reqKey) return;

        const map = this._similarAvailability || {};
        const $wrap = $('[data-repeat="similarUnits.units"]');
        if (!$wrap.length) return;

        $wrap.find('[data-id]').each((_, el) => {
            const $card = $(el);
            const id = String($card.attr('data-id') || '').trim();
            if (!id) return;

            const v = Object.prototype.hasOwnProperty.call(map, id) ? map[id] : null;

            const $badge = $card.find('.js-similar-badge').first();
            const $link  = $card.find('a.js-similar-link').first();

            // Cache default label once (English comments as requested)
            if ($link.length && !$link.attr('data-default-label')) {
                $link.attr('data-default-label', $link.text().trim() || t('check_availability', 'Check Availability'));
            }

            const defaultLabel = $link.attr('data-default-label') || t('check_availability', 'Check Availability');
            const seeLabel     = t('see_this_apartment', 'See this apartment');

            // Unknown state (no info): hide badge, link back to default and visible
            if (v === null) {
                if ($badge.length) {
                    $badge.addClass('hidden').text('');
                }
                if ($link.length) {
                    $link.removeClass('hidden');
                    $link.text(defaultLabel);
                    $link.removeClass('bg-basel-blue text-white border-basel-blue hover:bg-basel-blue-dark')
                        .addClass('border-gray-300 text-gray-800 hover:bg-gray-50');
                }
                return;
            }

            if (v === true) {
                if ($badge.length) {
                    $badge.removeClass('hidden')
                        .text(t('available', 'Available'))
                        .removeClass('bg-gray-50 text-gray-500 border-gray-200 bg-gray-100 text-gray-700 border-gray-200')
                        .addClass('bg-green-100 text-green-700 border-green-200');
                }
                if ($link.length) {
                    // Availability exists: show CTA but don't call it "Book"
                    $link.removeClass('hidden');
                    $link.text(seeLabel);
                    $link.removeClass('border-gray-300 text-gray-800 hover:bg-gray-50')
                        .addClass('bg-basel-blue text-white border-basel-blue hover:bg-basel-blue-dark');
                }
            } else {
                if ($badge.length) {
                    $badge.removeClass('hidden')
                        .text(t('not_available', 'Not available'))
                        .removeClass('bg-gray-50 text-gray-500 border-gray-200 bg-green-100 text-green-700 border-green-200')
                        .addClass('bg-gray-100 text-gray-700 border-gray-200');
                }
                if ($link.length) {
                    // No availability: don't show "Check Availability" button at all
                    $link.addClass('hidden');
                }
            }
        });
    }

    getSimilarApartmentIds() {
        // Prefer API "similarApartmentIds" if it exists
        const raw = this.apartmentData?.similarApartmentIds;

        let ids = [];
        if (Array.isArray(raw)) {
            ids = raw;
        } else {
            // fallback: derive from similarUnits.units
            const units = this.apartmentData?.similarUnits?.units;
            if (Array.isArray(units)) {
                ids = units.map(it => it?.externalId ?? it?.id ?? '').filter(Boolean);
            }
        }

        // normalize, unique, remove main id
        const main = String(this.apartmentId || '');
        const uniq = Array.from(new Set(ids.map(x => String(x).trim()).filter(Boolean)));
        return uniq.filter(id => id !== main);
    }

    getAvailabilityPayloadIds() {
        const main = String(this.apartmentId || '').trim();
        const similars = this.getSimilarApartmentIds();
        // ensure main is not duplicated in similars
        return {
            mainId: main,
            similarIds: similars.filter(id => id !== main)
        };
    }

    resetSimilarAvailabilityUI() {
        const $wrap = $('[data-repeat="similarUnits.units"]');
        if (!$wrap.length) return;

        $wrap.find('[data-id]').each((_, el) => {
            const $card = $(el);

            const $badge = $card.find('.js-similar-badge').first();
            const $link  = $card.find('a.js-similar-link').first();

            if ($badge.length) {
                $badge.addClass('hidden')
                    .text('')
                    .removeClass('bg-green-100 text-green-700 border-green-200 bg-gray-100 text-gray-700 border-gray-200')
                    .addClass('bg-gray-50 text-gray-500 border-gray-200');
            }

            if ($link.length) {
                // Cache default label once
                if (!$link.attr('data-default-label')) {
                    $link.attr('data-default-label', $link.text().trim() || t('check_availability', 'Check Availability'));
                }

                const defaultLabel = $link.attr('data-default-label') || t('check_availability', 'Check Availability');

                $link.text(defaultLabel);

                // Revert to default (non-primary) styling
                $link.removeClass('bg-basel-blue text-white border-basel-blue hover:bg-basel-blue-dark')
                    .addClass('border-gray-300 text-gray-800 hover:bg-gray-50');
            }
        });

        this._similarAvailability = null;
        this._similarAvailKey = null;
    }

    // Returns a native Promise, so .finally() works
    // single endpoint, returns jqXHR (jQuery Deferred)
    // single endpoint, returns jqXHR (jQuery Deferred)
    fetchUnitAvailability({ dateFrom, dateTo, guests, similarApartmentIds = null, _key }) {
        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && BL_AJAX.nonce) || '';
        const apartmentId = (this.apartmentId || '').toString();

        const reqKey = _key || `${dateFrom}|${dateTo}|${guests}`;

        const base   = this.apartmentDataFromResh ? this.extractReshPricing() : this.getMicroserviceBaseline();

        const priceFromType = base.priceFromType || 'DAILY';

        // ✅ if we pass similarApartmentIds -> we intentionally "shorten" request
        const includeSimilar = Array.isArray(similarApartmentIds) && similarApartmentIds.length > 0;

        const data = {
            action: 'bl_check_availability',
            _ajax_nonce: nonce,
            lang: this.lang,
            dateFrom, dateTo,
            apartmentId,
            priceFromType,
            guests: String(guests)
        };

        // keep previous params ONLY when we are not doing "similar" mode
        if (!includeSimilar) {
            data.buildingId   = (this.buildingId || '').toString();
            data.unitTypeType = (this.apartmentData?.specifications?.bedroomType || '').toString();
            data.maxGuests    = (this.apartmentData?.specifications?.maxOccupancy || '').toString();
        } else {
            // send similar ids as "similarApartmentIds[]"
            data['similarApartmentIds[]'] = similarApartmentIds.map(String);
        }

        const xhr = $.ajax({
            url: ajaxUrl,
            method: 'POST',
            dataType: 'json',
            data
        });

        xhr.done((res) => {
            // allow both success and error to still carry similarAvailability
            const payload = res?.data || {};
            const similarMap = payload?.similarAvailability;

            if (similarMap && typeof similarMap === 'object') {
                this._similarAvailability = similarMap;
                this._similarAvailKey = reqKey;
                this.updateSimilarAvailabilityUI(reqKey);
            }

            if (!res || !res.success) return;

            const unitRow = payload.unit || payload;

            if (this._availKey === reqKey) {
                this.apartmentDataFromResh = unitRow;
                this.buildingId = unitRow?.buildingId;
                this._lastFetchedDates = { dateFrom, dateTo };
            }
        });

        xhr.fail((jq) => {
            // When PHP responds with wp_send_json_error(..., 404), jQuery routes it here.
            const payload = jq?.responseJSON?.data || {};
            const similarMap = payload?.similarAvailability;

            if (similarMap && typeof similarMap === 'object') {
                this._similarAvailability = similarMap;
                this._similarAvailKey = reqKey;
                this.updateSimilarAvailabilityUI(reqKey);
            }
        });

        // save a marker to protect against race conditions
        xhr._reqKey = reqKey;
        return xhr;
    }

    showUnavailableUI($scope, msg) {
        const $break = $scope;
        const $info  = $('.js-long-info');
        $info.empty();
        $break.html(
            `<div class="mt-2 text-sm text-red-600">${this.escapeHtml(msg || t('apartment_is_not_available', 'The apartment is not available on these dates.'))}</div>`
        );
    }

    clearAvailabilityUI($scope) {
        const $break = $scope;
        if ($break.find('.text-red-600').length) $break.empty();
    }

    extractReshPricing() {
        // rateAvailabilities can be an array or an object
        const ra = this.apartmentDataFromResh?.rateAvailabilities?.[0]
            || this.apartmentDataFromResh?.rateAvailabilities
            || {};

        const priceFromGross = Number(ra?.avgTotals?.gross ?? 0) || 0;
        const priceFromGrossNet = Number(ra?.avgTotals?.net ?? 0) || 0;
        const priceFromType  = String(ra?.rateType || 'DAILY').toUpperCase();
        const vatAmount    = Number(ra?.totals?.vat || 0); // e.g., 0.077
        const totalsGrossNet    = Number(ra?.totals?.net || 0); // e.g., 0.077
        const totalsGross    = Number(ra?.totals?.gross ?? 0) || 0;

        // 1) take from API if present
        let nights = Number(ra?.nights ?? NaN);

        // 2) if API no longer returns it — compute from selected dates
        if (!Number.isFinite(nights) || nights <= 0) {
            // take the last dates used for fetch, or the current values in fields
            const df = this._lastFetchedDates?.dateFrom || this.dates?.checkIn  || '';
            const dt = this._lastFetchedDates?.dateTo   || this.getEffectiveCheckoutDate(df, this.dates?.checkOut || '');
            nights = this.computeNights(df, dt);
        }

        // min stay from API or default based on rate type
        const minStayRaw = Number(ra?.minStay ?? 0);

        return {
            priceFromGrossNet,
            priceFromGross,               // display in header
            priceFromType,                // 'DAILY' | 'MONTHLY'
            vatAmount,
            totalsGrossNet,
            totalsGross,                  // total for the interval from API
            nights,                       // ← now always computed
            minStayDays: (minStayRaw > 0 ? minStayRaw : (priceFromType === 'MONTHLY' ? 28 : 1))
        };
    }


    toFeatureId(slug = '') {
        slug = String(slug).toLowerCase().trim();
        slug = slug.replace(/\s+/g, '_');
        slug = slug.replace(/[^a-z0-9_-]+/g, '_');
        slug = slug.replace(/_+/g, '_');
        slug = slug.replace(/^[_-]+|[_-]+$/g, '');
        return slug;
    }

    getHiddenExtrasSlugsSet() {
        // Build once per page load (or until invalidated)
        if (this._hiddenExtrasSlugsSet instanceof Set) return this._hiddenExtrasSlugsSet;

        const rows = (window.apartment && apartment.hiddenExtras) ? apartment.hiddenExtras : [];
        const set = new Set();

        if (Array.isArray(rows)) {
            rows.forEach((row) => {
                // ACF repeater expected shape: [{ slug: "..." }, ...]
                const raw = (row && typeof row === 'object') ? (row.slug ?? '') : row;
                const n = this.toFeatureId(raw);
                if (n) set.add(n);
            });
        }

        this._hiddenExtrasSlugsSet = set;
        return set;
    }

    isExtraHiddenBySlug(slug) {
        const s = this.toFeatureId(slug);
        if (!s) return false;
        return this.getHiddenExtrasSlugsSet().has(s);
    }

    /**
     * Remove hidden extras from selected state + URL (so totals won't include invisible extras)
     */
    pruneHiddenSelectedExtrasByList(list) {
        const hidden = this.getHiddenExtrasSlugsSet();
        if (!hidden || hidden.size === 0) return;

        let changed = false;

        for (let i = 0; i < (list || []).length; i++) {
            const svc = list[i] || {};
            const id = String(svc.externalId ?? svc.id ?? svc.extraId ?? '').trim();
            if (!id) continue;

            const slug = this.getExtraSlugFromService(svc); // already uses toFeatureId inside
            if (!slug) continue;

            if (hidden.has(slug) && Object.prototype.hasOwnProperty.call(this.addedExtraServices || {}, id)) {
                delete this.addedExtraServices[id];
                changed = true;
            }
        }

        if (changed) {
            const ids = Object.keys(this.addedExtraServices || {}).sort();
            this.updateUrlParams({ extras: ids.length ? ids.join(',') : null });
        }
    }

    hydrateExtrasFromUrl() {
        const raw = (this.getUrlParam('extras') || '').trim();
        if (!raw) { this.addedExtraServices = {}; return; }

        const requested = Array.from(new Set(
            raw.split(',').map(s => String(s).trim()).filter(Boolean)
        ));

        const list = this.filterExtrasByCategory(this.getAllExtrasList(), this.getCurrentStayCategory());

        const available = new Map();
        for (const s of list) {
            if (!s || s.includedInRate) continue; // не додаємо ті, що вже включені
            const id = String(s.externalId ?? s.id ?? s.extraId ?? '').trim();
            if (!id) continue;
            available.set(id, {
                price: Number(s.netPrice ?? s.price ?? 0) || 0,
                freq : String(s.billing_frequency ?? s.frequency ?? '').toLowerCase()
            });
        }

        const next = {};
        const validIds = [];
        for (const rid of requested) {
            const id = String(rid);
            if (!available.has(id)) continue;
            next[id] = available.get(id);
            validIds.push(id);
        }
        this.addedExtraServices = next;

        if (validIds.length !== requested.length) {
            this.updateUrlParams({ extras: validIds.length ? validIds.join(',') : null });
        }
    }

    get longInfoHTML(){
        const longTermRentalRequirements  = (window.BL_AJAX && BL_AJAX.long_term_rental_requirements) || '';
        const longTermRentalSuccess  = `<div class="mt-2 mb-2 text-sm text-green-500">${this.escapeHtml(t('long_apartment_available', 'Apartment is available. Please see requirements for long-term rental and send an enquiry.'))}</div>`;

        return longTermRentalRequirements? `${longTermRentalSuccess}<div class="bg-green-100 border border-green-500 rounded-lg p-4">
          <div class="text-sm text-grey-900">
            ${longTermRentalRequirements}
          </div>
        </div>`:'';
    }

    getUrlParam(name) {
        try { return new URLSearchParams(window.location.search).get(name) ?? ''; }
        catch (e) { return ''; }
    }
    getUrlParamInt(name) {
        const n = parseInt(this.getUrlParam(name), 10);
        return Number.isFinite(n) ? n : 0;
    }
    getUrlParamDate(name) {
        const v = this.getUrlParam(name);
        if (!v) return '';
        return this.parseISO(v) ? v.slice(0, 10) : '';
    }

    hasUnsureCheckoutParam() {
        try {
            return new URLSearchParams(window.location.search).has(this.unsureCheckoutParam || 'unsureCheckout');
        } catch (e) {
            return false;
        }
    }

    isUnsureCheckoutActive() {
        this.unsureCheckoutActive = this.hasUnsureCheckoutParam();
        return this.unsureCheckoutActive;
    }

    getUnsureCheckoutDays() {
        const raw = window.BL_AJAX?.number_of_days_for_departure_date;
        const days = parseInt(raw, 10);

        return Number.isFinite(days) && days > 0 ? days : 60;
    }

    getUnsureCheckoutInfoboxHtml() {
        return (window.BL_AJAX && BL_AJAX.im_not_sure_infobox)
            ? String(BL_AJAX.im_not_sure_infobox)
            : `<p>${t('im_not_sure_infobox_default', 'We used an estimated departure date so we can check availability for you. You can change the departure date at any time; once you do, we will use your selected date instead.')}</p>`;
    }

    getUnsureCheckoutCheckboxHtml() {
        const checked = this.hasUnsureCheckoutParam() ? 'checked' : '';

        return `
        <div class="flex flex-col sm:flex-row gap-4 sm:gap-6 mt-3">
            <div class="flex items-center space-x-2">
                <label class="flex items-center space-x-2 text-xs sm:text-sm text-gray-700 cursor-pointer">
                    <input
                        type="checkbox"
                        name="unsureCheckout"
                        value="true"
                        class="peer h-4 w-4 shrink-0 rounded-sm border border-primary"
                        data-role="unsure-checkout"
                        ${checked}
                    >
                    <span class="flex-1 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg"
                             width="24"
                             height="24"
                             viewBox="0 0 24 24"
                             fill="none"
                             stroke="currentColor"
                             stroke-width="2"
                             stroke-linecap="round"
                             stroke-linejoin="round"
                             class="lucide lucide-clock w-3 h-3 sm:w-4 sm:h-4 mr-1">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        ${t('checkout_unknown_full', "I'm not sure about check-out date")}
                    </span>
                </label>
            </div>
        </div>
    `;
    }

    getComputedUnsureCheckoutDate(dateFrom) {
        const parsed = this.parseISO(dateFrom);
        if (!parsed) return '';

        return this.formatISO(this.addDays(parsed, this.getUnsureCheckoutDays()));
    }

    getEffectiveCheckoutDate(dateFrom = '', dateTo = '') {
        const arrivalDate = dateFrom || this.dates?.checkIn || this.getUrlParamDate('dateFrom');

        if (this.hasUnsureCheckoutParam() && arrivalDate) {
            return this.getComputedUnsureCheckoutDate(arrivalDate);
        }

        return dateTo || this.dates?.checkOut || this.getUrlParamDate('dateTo') || '';
    }

    renderUnsureCheckoutInfobox() {
        const $box = $('#unsure-checkout-infobox');
        if (!$box.length) return;

        const hasArrivalDate = !!(this.dates?.checkIn || this.getUrlParamDate('dateFrom'));

        if (this.isUnsureCheckoutActive() && hasArrivalDate) {
            $box.html(this.getUnsureCheckoutInfoboxHtml()).removeClass('hidden');
        } else {
            $box.addClass('hidden').empty();
        }
    }

    setCheckoutValue($checkout, value, triggerChange = false) {
        const el = $checkout && $checkout[0];
        const fp = el && el._flatpickr;

        if (fp) {
            fp.setDate(value || null, triggerChange);
            return;
        }

        $checkout.val(value || '');
        if (triggerChange) $checkout.trigger('change');
    }

    syncUnsureCheckoutCheckboxes(checked) {
        $('[data-role="unsure-checkout"]').prop('checked', !!checked);
    }

    clearCheckoutFields() {
        $('[data-role="checkout"]').each((_, el) => {
            this.setCheckoutValue($(el), '', false);
        });

        this.dates.checkOut = '';
    }

    toggleUnsureCheckoutUI(checked = this.hasUnsureCheckoutParam()) {
        $('[data-role="checkout"]').each((_, el) => {
            const $checkout = $(el);
            const fp = el && el._flatpickr;

            if (checked) {
                this._suppressCheckoutChange = true;

                if (fp) {
                    fp.setDate(null, false);
                    fp.close();
                }

                $checkout.val('');

                $checkout
                    .val('')
                    .prop('disabled', true)
                    .attr('disabled', 'disabled')
                    .addClass('opacity-60 cursor-not-allowed');

                if (fp && fp.input) {
                    $(fp.input)
                        .val('')
                        .prop('disabled', true)
                        .attr('disabled', 'disabled')
                        .addClass('opacity-60 cursor-not-allowed');
                }

                setTimeout(() => {
                    this._suppressCheckoutChange = false;
                }, 0);
            } else {
                $checkout
                    .prop('disabled', false)
                    .removeAttr('disabled')
                    .removeClass('opacity-60 cursor-not-allowed');

                if (fp && fp.input) {
                    $(fp.input)
                        .prop('disabled', false)
                        .removeAttr('disabled')
                        .removeClass('opacity-60 cursor-not-allowed');
                }
            }
        });
    }

    applyUnsureCheckoutMode(dateFrom = '') {
        const arrivalDate = dateFrom || this.dates.checkIn || this.getUrlParamDate('dateFrom');

        this.unsureCheckoutActive = true;
        this.syncUnsureCheckoutCheckboxes(true);
        this.toggleUnsureCheckoutUI(true);

        this.updateUrlParams({
            unsureCheckout: 'true',
            dateFrom: arrivalDate || null,
            dateTo: null
        });

        this.renderUnsureCheckoutInfobox();
    }

    removeUnsureCheckoutMode() {
        this.unsureCheckoutActive = false;
        this.syncUnsureCheckoutCheckboxes(false);
        this.toggleUnsureCheckoutUI(false);

        if (this.hasUnsureCheckoutParam()) {
            this.updateUrlParams({ unsureCheckout: null });
        }

        this.renderUnsureCheckoutInfobox();
    }

    applyParamsFromUrl() {
        const guestsUrl = this.getUrlParamInt('guests');
        const legacyMg  = this.getUrlParamInt('maxGuests');
        const mg = guestsUrl || legacyMg;

        if (mg > 0) {
            this.guests = mg;  // only remember it; options will be created in renderGuestsSelect
            // URL migration: ?maxGuests= → ?guests=
            if (!guestsUrl && legacyMg) this.updateUrlParams({ guests: mg });
        }
    }

    getNested(obj, path) {
        if (!obj || !path) return null;
        return path.split('.').reduce((acc, key) => (acc != null ? acc[key] : null), obj);
    }

    getExtrasSum() {
        var sum = 0;
        for (var k in this.addedExtraServices) {
            if (!Object.prototype.hasOwnProperty.call(this.addedExtraServices, k)) continue;
            sum += Number(this.addedExtraServices[k]) || 0;
        }
        return sum;
    }

    getExtrasTotal(nights, guests = 1) {
        let sum = 0;
        for (const k in this.addedExtraServices) {
            if (!Object.prototype.hasOwnProperty.call(this.addedExtraServices, k)) continue;
            const v = this.addedExtraServices[k];
            if (v && typeof v === 'object') {
                const price = Number(v.price) || 0;
                const freq  = String(v.freq || '');
                const units = this.extrasUnitsByStay(freq, nights);
                sum += price * units;
            } else {
                // backward-compat: if saved as a number
                sum += Number(v) || 0;
            }
        }
        return sum;
    }

    _toMidnight(d) {
        if (!d) return null;
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }

    computeNights(ci, co){
        const a=this.parseISO(ci), b=this.parseISO(co);
        if(!a||!b) return 0;
        const ms = Date.UTC(b.getUTCFullYear(),b.getUTCMonth(),b.getUTCDate()) -
            Date.UTC(a.getUTCFullYear(),a.getUTCMonth(),a.getUTCDate());
        return Math.max(0, Math.floor(ms/86400000));
    }

    daysInMonthUTC(y, m){ return new Date(Date.UTC(y, m + 1, 0)).getUTCDate(); }

    countMonthlyUnitsByBoundary(ciStr, coStr){
        const ci = this.parseISO(ciStr);
        const co = this.parseISO(coStr);
        if (!ci || !co || co <= ci) return 0;
        // find minimal m where co <= ci + m months
        let m = 1;
        while (co > this.addMonthsClamp(ci, m)) m++;
        return m;
    }

    addMonthsClamp(d, m){
        const y  = d.getUTCFullYear();
        const mo = d.getUTCMonth();
        const dd = d.getUTCDate();
        const tMo = mo + m;
        const ty  = y + Math.floor(tMo / 12);
        const tm  = ((tMo % 12) + 12) % 12;
        const dim = this.daysInMonthUTC(ty, tm);
        const td  = Math.min(dd, dim);
        return new Date(Date.UTC(ty, tm, td));
    }

    initDatepickers($container, cfg) {
        const $ci = $container.find('[data-role="checkin"]');
        const $co = $container.find('[data-role="checkout"]');

        const today = (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();

        // hosts for popups
        const ciHost = $ci.parent().addClass('relative')[0];
        const coHost = $co.parent().addClass('relative')[0];

        const ensureMobilePlacement = (inst) => {
            // only on mobile breakpoint
            if (!window.matchMedia('(max-width: 1023px)').matches) return;

            // always try to open ABOVE
            inst.set('position', 'above');

            const cal = inst.calendarContainer;
            cal.classList.remove('fp-center'); // reset previous state

            // after opening, measure if it fits above the input
            requestAnimationFrame(() => {
                const ch = cal.getBoundingClientRect().height || 0;
                const topAvail = inst._positionElement.getBoundingClientRect().top; // space to top of viewport
                if (ch + 16 > topAvail) {
                    // doesn't fit — show as centered overlay
                    cal.classList.add('fp-center');
                }
            });
        };

        flatpickr.localize({
            ...(IS_DE ? German : {}),
            firstDayOfWeek: 1
        });

        if (typeof flatpickr === 'function') {
            const ciPicker = $ci[0]._flatpickr || flatpickr($ci[0], {
                allowInput: true,
                dateFormat: 'Y-m-d',
                defaultDate: this.dates?.checkIn || null,
                disableMobile: true,
                minDate: today,
                // anchored to the input
                static: true,
                appendTo: ciHost,
                positionElement: $ci[0],
                position: 'above', // ← default is above
                onOpen: [
                    (sel, str, inst) => {
                        if (!inst.selectedDates.length) inst.jumpToDate(inst.config.minDate || today);
                        ensureMobilePlacement(inst);
                    }
                ],
                onChange: [ () => { $ci.trigger('change'); } ],
                onClose:  [ (sel, str, inst) => inst.calendarContainer.classList.remove('fp-center') ]
            });

            const coPicker = $co[0]._flatpickr || flatpickr($co[0], {
                allowInput: true,
                dateFormat: 'Y-m-d',
                defaultDate: this.dates?.checkOut || null,
                disableMobile: true,
                minDate: today,
                static: true,
                appendTo: coHost,
                positionElement: $co[0],
                position: 'above',
                onOpen: [ (_, __, inst) => {
                    const ci = ciPicker.selectedDates[0];
                    const minCoDate = ci ? this.addDays(ci, cfg.minStayDays) : today;
                    coPicker.set('minDate', minCoDate);
                    const sel = coPicker.selectedDates[0];
                    coPicker.jumpToDate(sel && sel >= minCoDate ? sel : minCoDate);
                    ensureMobilePlacement(inst);
                }],
                onChange: [ () => { $co.trigger('change'); } ],
                onClose:  [ (sel, str, inst) => inst.calendarContainer.classList.remove('fp-center') ]
            });

            const reconfigCO = () => {
                const ci = ciPicker.selectedDates[0];
                if (ci) {
                    const minCoDate = this.addDays(ci, cfg.minStayDays);
                    coPicker.set('minDate', minCoDate);
                    const sel = coPicker.selectedDates[0];
                    if (sel && sel < minCoDate) { coPicker.clear(); $co.trigger('change'); }
                    if (coPicker.isOpen) coPicker.jumpToDate(sel && sel >= minCoDate ? sel : minCoDate);
                } else {
                    coPicker.set('minDate', today);
                    if (coPicker.isOpen) coPicker.jumpToDate(today);
                }
            };



            const ciStr = $ci.val();
            const coStr = $co.val();
            if (ciStr) ciPicker.setDate(ciStr, false);
            if (coStr) coPicker.setDate(coStr, false);

            // одразу оновлюємо мінімально дозволену дату check-out
            reconfigCO();
            ciPicker.config.onChange.push(() => { reconfigCO(); });

            this._ciPicker = ciPicker;
            this._coPicker = coPicker;
            return;
        }

        // --- Fallback without flatpickr ---
        const reconfigMinAttr = () => {
            const ciStr = $ci.val();
            const minBase = today; // never allow past dates
            $ci.attr('min', this.formatISO(minBase));

            if (ciStr) {
                const minCoStr = this.formatISO(this.addDays(this.parseISO(ciStr), cfg.minStayDays));
                $co.attr('min', minCoStr);
                const coStr = $co.val();
                if (coStr && this.parseISO(coStr) < this.parseISO(minCoStr)) {
                    $co.val('').trigger('change');
                }
            } else {
                $co.attr('min', this.formatISO(minBase));
            }
        };

        $ci.on('focus change blur input', reconfigMinAttr);
        reconfigMinAttr();
    }

    initSelects($scope) {
        const self = this;
        const $sel = $scope.find('[data-role="guests"]');

        $sel.each(function () {
            if ($(this).hasClass('select2-hidden-accessible')) {
                $(this).select2('destroy');
            }

            $(this).select2({
                minimumResultsForSearch: Infinity,   // no search
                width: '100%',
                dropdownParent: $(this).closest('.select-box'),
                dropdownAutoWidth: true,
                placeholder: t('guests','Guests'),
                theme: 'basel'
            }).on('change', function () {
                const val = parseInt($(this).val(), 10) || 1;
                self.guests = val;
                self.updateUrlParams({ guests: val });

                self.refreshPricing();
            });
        });

        const sp = new URLSearchParams(window.location.search);
        const hasGuestsParam = sp.has('guests') || sp.has('maxGuests');
        if (!hasGuestsParam) {
            const def = this.guests;
            if (Number.isFinite(def) && def > 0) {
                this.updateUrlParams({ guests: def });
            }
        }
    }

    currencyFormat(value, currency, locale) {
        var curr = currency || "CHF";
        var loc  = locale   || "de-CH";
        var num  = Number(value);
        if (!Number.isFinite(num)) return "";
        try {
            return new Intl.NumberFormat(loc, {
                style: "currency",
                currency: curr,
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            }).format(num);
        } catch (e) {
            return "";
        }
    }

    currencyFormatTwoDecimals(value, currency, locale) {
        var curr = currency || "CHF";
        var loc  = locale   || "de-CH";
        var num  = Number(value);
        if (!Number.isFinite(num)) return "";
        try {
            return new Intl.NumberFormat(loc, {
                style: "currency",
                currency: curr,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(num);
        } catch (e) {
            return "";
        }
    }

    computeDerived(data) {
        const out = { pricing: {} };
        const p = data?.pricing || {};
        const vatRate = Number(p.vatRate || 0);

        // nightly price incl. VAT
        const net = Number(p.pricePerNight ?? NaN);
        if (!Number.isNaN(net)) {
            out.pricing.totalPerNight = net * (1 + vatRate);
            out.pricing.hasPrice = true;
        } else {
            out.pricing.hasPrice = false;
        }

        // discount percent (from originalPrice to pricePerNight)
        if (p.originalPrice && p.pricePerNight && p.originalPrice > p.pricePerNight) {
            out.pricing.discountPercent = (p.originalPrice - p.pricePerNight) / p.originalPrice;
        }

        return out;
    }

    ordinal(n) {
        const v = Math.abs(parseInt(n, 10));
        if (!Number.isFinite(v)) return String(n);
        const mod100 = v % 100;
        if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
        switch (v % 10) {
            case 1: return `${n}st`;
            case 2: return `${n}nd`;
            case 3: return `${n}rd`;
            default: return `${n}th`;
        }
    }

    ordinalDe(n) {
        const v = Math.abs(parseInt(n, 10));
        if (!Number.isFinite(v)) return String(n);
        return `${n}`;
    }

    applyFormat(val, fmt, root) {
        if (!fmt) return val;
        const [name, arg] = fmt.split(':');
        const currency = (arg || root?.dataset?.currency || 'CHF').trim();
        const locale = root?.dataset?.locale || undefined;

        switch (name) {
            case 'currency': {
                return this.currencyFormat(val, currency, locale);
            }

            case 'percent0': {
                const n = this.toNumber(val);
                return n == null ? '' : `${Math.round(n * 100)}%`;
            }

            case 'area': {
                let candidate = val;
                if (val && typeof val === 'object') {
                    candidate = val.value != null ? val.value
                        : (val.min != null ? val.min
                            :  val.max);
                }
                const n = this.toNumber(candidate);
                return n == null ? '' : `${n} m²`;
            }

            case 'bedrooms': {
                const n = this.toNumber(val);
                if (n == null) return '';
                return n === 0 ? 'Studio' : String(n);
            }

            case 'floor': {
                const n = this.toNumber(val);
                if (n == null) return '';
                return IS_DE ? this.ordinalDe(n) : this.ordinal(n);
            }

            default:
                return val;
        }
    }

    setText(elOr$el, value) {
        const el = elOr$el && elOr$el.jquery ? elOr$el[0] : elOr$el;
        if (!el) return;
        el.textContent = value == null ? '' : String(value);
    }

    toggleIf(elOr$el, truthy) {
        const $el = elOr$el && elOr$el.jquery ? elOr$el : $(elOr$el);
        if (!$el.length) return;
        if (truthy) {
            $el.prop('hidden', false).removeClass('is-hidden');
        } else {
            $el.prop('hidden', true).addClass('is-hidden');
        }
    }

    updateOverallVisibility($scope) {
        var $sc = $scope && $scope.jquery ? $scope : $($scope || document);

        $sc.find('.bind-container-overall').each(function () {
            var $overall = $(this);
            var hasAny = $overall.find('.bind-container.-visible').length > 0;
            $overall.toggleClass('-visible', hasAny);
        });
    };

    toNumber(val) {
        const n = Number(val);
        return Number.isFinite(n) ? n : null;
    }

    bindSimple(root, ctx) {
        var self = this;
        var $root = root && root.jquery ? root : $(root);

        // reset
        $root.find('.bind-container.-visible').removeClass('-visible');

        // data-if
        $root.find('[data-if]').each(function () {
            var $el = $(this);
            var condPath = getLocalizedPath($el.data('if'));
            var val = self.getNested(ctx, condPath);
            self.toggleIf($el, !!val);
            if (val) $el.closest('.bind-container').addClass('-visible');
        });

        // data-bind
        $root.find('[data-bind]').each(function () {
            var $el = $(this);
            var rawPath = $el.data('bind');
            var path = getLocalizedPath(rawPath);
            var val = self.getNested(ctx, path);
            val = self.applyFormat(val, $el.data('format'), $root[0]);

            if ($el.is('[data-html]')) $el.html(val == null ? '' : String(val));
            else self.setText($el, val);

            var has = val != null && String(val).trim() !== '';
            if (has)  {
                $el.closest('.bind-container').addClass('-visible');
            }
        });

        // data-attr
        $root.find('[data-attr]').each(function () {
            var $el = $(this);
            var attrs = String($el.data('attr') || '')
                .split(',').map(function (s){return $.trim(s);}).filter(Boolean);
            var rawPath = $el.data('bind');
            var path = getLocalizedPath(rawPath);
            var val = self.getNested(ctx, path);
            val = self.applyFormat(val, $el.data('format'), $root[0]);

            var has = val != null && String(val).trim() !== '';
            $.each(attrs, function(_, a){
                if (!has) $el.removeAttr(a); else $el.attr(a, String(val));
            });
            if (has) $el.closest('.bind-container').addClass('-visible');
        });

        this.updateOverallVisibility($root);
    }

    _clearRepeatContainer(container) {
        container.querySelectorAll(":scope > :not(template)").forEach(n => n.remove());
    }

    // Generic fallback renderer using <template> (your previous logic)
    buildDefaultRepeat($container, arr, $root, ctx) {
        var self = this;
        var $tpl = $container.find("template").first();
        if (!$tpl.length) return;

        // Use template inner HTML and build jQuery nodes from it
        var tplHtml = $tpl.html();

        $.each(arr, function (_, item) {
            // If item is a primitive, expose it as { value: item }
            var localCtx = (item !== Object(item)) ? { value: item } : item;

            // Clone nodes from template HTML
            var $nodes = $(tplHtml);

            // Bind data inside cloned nodes
            self.bindSimple($nodes, localCtx);
            self.bindRepeats($nodes, localCtx); // nested repeats if needed

            // Append to container
            $container.append($nodes);
        });
    }

    escapeHtml(s) {
        return String(s).replace(/[&<>"']/g, function (m) {
            return ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" })[m];
        });
    };

    buildFeatures($container, arr, opts = {}) {
        const limit = Number(opts.limit || 0);
        // If empty -> hide (when data-empty-hide present)
        if (!$container || !$container.length) return;
        if (!$.isArray(arr) || !arr.length) {
            if ($container.is("[data-empty-hide]")) $container.prop("hidden", true);
            return;
        }
        $container.prop("hidden", false);

        const ordered = this._prioritizeFeatures(arr).map(it => {
            const name = typeof it === 'string' ? it : (it?.name || '');
            const de_name = typeof it === 'string' ? it : (it?.nameLang2 || '');
            return {
                iconClass: it?.iconClass || '',
                label: name,
                label_de: de_name
            };
        });

        const list = limit > 0 ? ordered.slice(0, limit) : ordered;

        var html = "";

        for (let i = 0; i < list.length; i++) {
            const icon = list[i].iconClass ? `<i class="${list[i].iconClass}"></i>` : '';
            const label = IS_DE ? (list[i].label_de || "") : (list[i].label || "");

            html += ''
                + '<div class="flex flex-col items-center text-center p-2 sm:p-3 rounded-lg">'
                +   `<div class="mb-2">${icon}</div>`
                +   `<span class="text-xs sm:text-sm text-gray-700 leading-tight">${this.escapeHtml(label)}</span>`
                + '</div>';
        }

        // Replace content at once (keep any <template> if you had it)
        $container.children(":not(template)").remove();
        $container.append(html);
        if(html) $container.closest('.bind-container').addClass('-visible');
    }

    _getExtrasOrderMap() {
        // English comments as requested
        const map = Object.create(null);
        const arr = Array.isArray(this.extrasAssets) ? this.extrasAssets : [];

        for (let i = 0; i < arr.length; i++) {
            const raw = String(arr[i]?.slug || '').trim();
            if (!raw) continue;

            // Normalize to the same format as getExtraSlugFromService()
            const slug = this.toFeatureId(raw);
            if (!slug) continue;

            // Keep the first occurrence
            if (map[slug] == null) map[slug] = i;
        }

        return map;
    }

    buildExtraServices($container, services, $root) {
        if (!$container || !$container.length) return;

        const category = this.getCurrentStayCategory();
        let list = this.filterExtrasByCategory(services, category);

        // Sort by extras_assets order (stable)
        const orderMap = this._getExtrasOrderMap();
        list = (Array.isArray(list) ? list : [])
            .map((svc, origIdx) => {
                const slug = this.getExtraSlugFromService(svc); // already normalized
                const ord = (slug && orderMap[slug] != null) ? orderMap[slug] : Number.POSITIVE_INFINITY;
                return { svc, origIdx, ord };
            })
            .sort((a, b) => {
                if (a.ord !== b.ord) return a.ord - b.ord;
                return a.origIdx - b.origIdx; // keep original order for "unknown" or same ord
            })
            .map(x => x.svc);

        // drop invalid selections for current category
        this.pruneSelectedExtras(list);

        // drop selections that are hidden for this apartment (and fix URL)
        this.pruneHiddenSelectedExtrasByList(list);

        if (!$.isArray(services) || !services.length) {
            if ($container.is("[data-empty-hide]")) $container.prop("hidden", true);
            return;
        }
        $container.prop("hidden", false);

        const hiddenSet = this.getHiddenExtrasSlugsSet();

        const freqLabel = (f) => {
            const v = String(f || '').toLowerCase();
            if (v === 'monthly') return t('freq_monthly','monthly');
            if (v === 'weekly')  return t('freq_weekly','weekly');
            if (v === 'daily')  return t('freq_daily','daily');
            return t('freq_one_off','one-off');
        };

        var currency = ($root && $root.data("currency")) || "CHF";
        var locale   = ($root && $root.data("locale"))   || "de-CH";

        var html = "";
        for (var i = 0; i < list.length; i++) {
            var svc = list[i] || {};

            if(!svc.includedInRate) {
                var id   = String(svc.id ?? '');
                var name = IS_DE ? (svc.descriptionLang2 || "") : (svc.name || "");
                const desc = (() => {
                    const src = String(svc.webDescription || '').trim();
                    if (!src) return '';
                    const parts = src.split('/').map(p => p.trim()).filter(Boolean);
                    if (!parts.length) return '';
                    return IS_DE ? parts[parts.length - 1] : parts[0];
                })();
                var price = Number(svc.price) || 0;

                // whether it's already added (persist state across rerenders)
                var isAdded = this.addedExtraServices && Object.prototype.hasOwnProperty.call(this.addedExtraServices, id);
                var freqRaw   = svc.billing_frequency ? String(svc.billing_frequency).trim() : '';
                var freqLower = freqRaw.toLowerCase();

                const slug = this.getExtraSlugFromService(svc);

                // skip hidden extras for this apartment
                if (hiddenSet && hiddenSet.size && slug && hiddenSet.has(slug)) {
                    // safety: ensure it's not selected (should already be pruned above)
                    if (isAdded) {
                        delete this.addedExtraServices[id];
                        isAdded = false;
                    }
                    continue;
                }

                // ensure "default" row exists in repeater too
                this.queueMissingExtraSlug('default');

                // if slug is new — queue it for saving to options
                this.queueMissingExtraSlug(slug);

                const iconUrl = this.getExtraIconUrlBySlug(slug);
                const logoUrl = this.getExtraLogoUrlBySlug(slug);

                const iconHtml = iconUrl
                    ? `<img class="w-8 h-8 object-contain" src="${this.escapeHtml(iconUrl)}" alt="" loading="lazy" />`
                    : '';

                const partnerHtml = logoUrl ? `
                <div class="sm:flex shrink-0 items-center justify-center logo-box">
                  <div class="flex flex-col items-center sm:items-start gap-2">
                    <img class="w-24 h-24 object-contain" src="${this.escapeHtml(logoUrl)}" alt="" loading="lazy" />
                  </div>
                </div>
                ` : '';

                html += `
            <div class="bg-white p-4 border border-blue-100 rounded-lg transition-colors">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-0 extras-box">
                    <!-- Col 1: Icon -->
                    <div class="flex items-center justify-center gap-3 shrink-0 icon-box">
                        ${iconHtml ? `<span class="inline-flex items-center justify-center">${iconHtml}</span>` : ''}
                    </div>

                    <!-- Col 2: API info -->
                    <div class="info-box">
                        <div class="flex flex-wrap items-center gap-2">
                        <h3 class="font-medium text-basel-gray text-sm sm:text-base break-words">
                            ${name}
                        </h3>
                        ${freqLower ? `
                            <span class="text-xs bg-gray-100 text-gray-600 border border-gray-300 rounded-full px-2 py-0.5">
                              ${freqLabel(freqLower)}
                            </span>
                        ` : ''}
                        </div>

                        ${desc ? `<p class="mt-1 text-xs sm:text-sm text-gray-600">${desc}</p>` : ''}
                    </div>

                    <div class="flex items-baseline gap-2 price-box">
                        ${price ? `
                            <div class="font-bold text-basel-gray text-sm sm:text-base">
                              ${this.currencyFormatTwoDecimals(price, currency, locale)}
                            </div>
                        ` : ''}
                    </div>

                    <!-- Col 3: Partner Logo -->
                    ${partnerHtml}

                    <!-- Col 4: CTA -->
                    <div class="flex sm:ml-auto shrink-0 justify-end cta-box">
                        <button type="button"
                        class="js-extra-toggle text-xs sm:text-sm px-3 py-2 rounded-md inline-flex items-center justify-center min-w-[92px]
                            ${isAdded ? 'bg-primary text-white' : 'border border-gray-300 text-gray-800 hover:bg-gray-50'}"
                        data-service-id="${id}"
                        data-service-price="${price}"
                        data-service-frequency="${freqLower}"
                        aria-pressed="${isAdded ? 'true' : 'false'}">
                            ${isAdded
                    ? `<svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                   <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path>
                                 </svg>${t('added', 'Added')}`
                    : t('add', 'Add')
                }
                        </button>
                    </div>
                </div>
            </div>
            `;
            }
        }

        $container.children(":not(template)").remove();
        $container.append(html);
        this.flushMissingExtrasIcons();
        if(html) $container.closest('.bind-container').addClass('-visible');
    }

    addForwardParamsForLinks(baseUrl) {
        if (!baseUrl) return baseUrl;

        try {
            const url = new URL(baseUrl, window.location.origin);
            const cur = new URLSearchParams(window.location.search);

            const dateFrom = this.dates?.checkIn || cur.get('dateFrom') || '';
            const dateTo = this.dates?.checkOut || cur.get('dateTo') || '';
            const guests = this.guests || cur.get('guests') || '';

            const hasUnsureCheckout = cur.has('unsureCheckout') && !!dateFrom;

            url.searchParams.delete('dateFrom');
            url.searchParams.delete('dateTo');
            url.searchParams.delete('guests');
            url.searchParams.delete('unsureCheckout');

            if (dateFrom) {
                url.searchParams.set('dateFrom', dateFrom);
            }

            if (guests) {
                url.searchParams.set('guests', String(guests));
            }

            if (hasUnsureCheckout) {
                url.searchParams.set('unsureCheckout', 'true');
            } else if (dateTo) {
                url.searchParams.set('dateTo', dateTo);
            }

            if (cur.get('stayType') === 'DAILY') {
                url.searchParams.set('stayType', 'DAILY');
            }

            return url.toString();
        } catch {
            return baseUrl;
        }
    }

    refreshSimilarLinks() {
        $('a.js-similar-link').each((_, el) => {
            const $link = $(el);
            const base = $link.data('baseHref') || $link.attr('data-base-href') || $link.attr('href');

            if (!base) return;

            $link.attr('href', this.addForwardParamsForLinks(base));
        });
    }

    fetchSimilarUnitMeta(unitIds = []) {
        const ajaxUrl = (window.BL_AJAX && BL_AJAX.url) || '/wp-admin/admin-ajax.php';
        const nonce   = (window.BL_AJAX && BL_AJAX.nonce) || '';
        const ids = Array.from(new Set((unitIds || []).map(String))).slice(0, 12);

        if (!ids.length) return $.Deferred().resolve({}).promise();

        return $.ajax({
            url: ajaxUrl,
            method: 'POST',
            dataType: 'json',
            data: {
                action: 'bl_get_unit_titles',
                _ajax_nonce: nonce,
                unitIds: ids.join(','),
                lang: this.lang
            }
        }).then(res => {
            const map = {};
            const items = res?.data?.items || [];
            items.forEach(it => {
                if (it?.unitId && it?.permalink) {
                    map[String(it.unitId)] = { title: it.title || '', permalink: it.permalink };
                }
            });
            return map; // unitId -> {title, permalink}
        }, () => ({}));
    }

    buildSimilarApartments($container, items, $root) {
        if (!$container || !$container.length) return;

        // очищаємо та показуємо легкий лоадер
        $container.children(":not(template)").remove();
        $container.append('<div class="p-4 text-sm text-gray-500">'+ t('loading_similar_apartments', 'Loading similar apartments…') +'</div>');

        // зібрати unitIds
        const unitIds = (Array.isArray(items) ? items : []).map(it => String(it.externalId ?? it.id ?? '')).filter(Boolean);

        this.fetchSimilarUnitMeta(unitIds).then((metaMap) => {
            // відфільтрувати лише ті, що мають WP-пост (перелік + тайтл з AJAX)
            const filtered = (items || []).filter(it => {
                const id = String(it.externalId ?? it.id ?? '');
                return !!(id && metaMap[id] && metaMap[id].permalink);
            });

            if (!filtered.length) {
                $container.children(":not(template)").remove();
                if ($container.is("[data-empty-hide]")) $container.prop("hidden", true);
                return;
            }
            $container.prop("hidden", false);

            const html = filtered.map((it) => {
                const id = String(it.externalId ?? it.id ?? '');
                const meta = metaMap[id] || {};
                const title = meta.title ? this.escapeHtml(meta.title) : (it.unitTypeName ? this.escapeHtml(it.unitTypeName) : "");
                const basePermalink = meta.permalink || "#";
                const permalink = this.addForwardParamsForLinks(basePermalink);

                const location = it.location ? this.escapeHtml(it.location) : "";
                const guests = it.maxGuests ? this.escapeHtml(it.maxGuests) : "";
                const unitName = it.unitName ? it.unitName : "";

                const areaMin = it.area?.min;
                const areaMax = it.area?.max;
                const areaLabel = (areaMin && areaMax && areaMin !== areaMax) ? `${areaMin}–${areaMax}m²`
                    : (areaMin || areaMax) ? `${areaMin || areaMax}m²` : "";

                const br = (it.bedRoomCount != null)
                    ? (Number(it.bedRoomCount) === 0 ? "Studio" : (Number(it.bedRoomCount)))
                    : "";

                const bathsNum = (it.bathrooms != null) ? Number(it.bathrooms) : null;
                const ba = (bathsNum != null && !Number.isNaN(bathsNum))
                    ? (bathsNum + " Bath" + (bathsNum === 1 ? "" : "s"))
                    : "";

                return `
                <div class="rounded-lg bg-card text-card-foreground shadow-sm border border-gray-200 hover:shadow-md transition-shadow" data-id="${this.escapeHtml(id)}">
                  <div class="p-3 sm:p-4">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                      <div class="flex-1">
                        <h3 class="font-medium text-basel-gray mb-1 text-sm sm:text-base">
                            ${title} 
                            <span class="js-similar-badge hidden inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-gray-50 text-gray-500 border-gray-200"></span>
                        </h3>
                        ${location ? `
                        <div class="flex items-center text-xs sm:text-sm text-gray-600 mb-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin w-3 h-3 sm:w-4 sm:h-4 mr-1"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg>
                          ${location}
                        </div>
                        ` : ''}
                        <div class="flex gap-4 text-xs sm:text-sm text-gray-600">
                          ${areaLabel ? `
                          <div class="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" class="lucide lucide-square w-3 h-3 sm:w-4 sm:h-4 mr-1 text-basel-blue" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <rect width="18" height="18" x="3" y="3" rx="2"></rect>
                            </svg>${this.escapeHtml(areaLabel)}
                          </div>
                          ` : ''}
                          ${br ? `
                          <div class="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" class="lucide lucide-bed w-3 h-3 sm:w-4 sm:h-4 mr-1 text-basel-blue" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M2 4v16"></path><path d="M2 8h18a2 2 0 0 1 2 2v10"></path><path d="M2 17h20"></path><path d="M6 8v9"></path>
                            </svg>${this.escapeHtml(br)}
                          </div>
                          ` : ''}
                          ${ba ? `
                          <div class="flex items-center">
                            <svg class="w-4 h-4 mr-1 text-basel-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path d="M9 11h6a4 4 0 0 1 4 4v5H5v-5a4 4 0 0 1 4-4z"></path>
                            </svg>${this.escapeHtml(ba)}
                          </div>
                          ` : ''}
                          ${guests ? `
                          <div class="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" class="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-basel-blue" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>${this.escapeHtml(guests)}
                          </div>
                          ` : ''}
                        </div>
                      </div>
                      <div class="flex-row items-center justify-between flex sm:flex-col sm:items-end">
                        <a class="js-similar-link text-xs sm:text-sm border border-gray-300 text-gray-800 hover:bg-gray-50 px-3 py-1 rounded-md"
                           data-base-href="${this.escapeHtml(basePermalink)}"
                            href="${this.escapeHtml(permalink)}" target="_blank">${t('check_availability', 'Check Availability')}</a>
                      </div>
                    </div>
                  </div>
                </div>`;

            }).join('');

            $container.children(":not(template)").remove();
            $container.append(html);

            this.refreshSimilarLinks();

            if (html) $container.closest('.bind-container').addClass('-visible');
            // Apply already fetched availability (if it exists for current key)
            if (this._similarAvailKey) {
                this.updateSimilarAvailabilityUI(this._similarAvailKey);
            } else {
                this.resetSimilarAvailabilityUI();
            }
        });
    }

    // ---- Nearby attractions helpers ----

    // Get apartment coordinates from PMS payload
    getApartmentCoords() {
        // coordinates are stored in: specifications.location.latitude / longitude
        const loc = this.apartmentData?.specifications?.location || {};
        const lat = Number(loc.latitude);
        const lng = Number(loc.longitude);

        console.log(loc);

        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            return { lat, lng };
        }
        return null;
    }

    // Haversine distance in meters between two points
    computeDistanceMeters(lat1, lng1, lat2, lng2) {
        const toRad = (v) => v * Math.PI / 180;
        const R = 6371000; // Earth radius in meters
        const dLat = toRad(lat2 - lat1);
        const dLng = toRad(lng2 - lng1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    // Map Google place types to our localized category label
    mapPlaceTypesToCategory(types = []) {
        const set = new Set(types.map(String));

        if (set.has('restaurant')) {
            return t('poi_restaurant', 'Restaurant');
        }
        if (set.has('cafe')) {
            return t('poi_cafe', 'Café');
        }
        if (set.has('supermarket') || set.has('grocery_or_supermarket')) {
            return t('poi_supermarket', 'Supermarket');
        }
        if (set.has('gym')) {
            return t('poi_gym', 'Gym');
        }
        if (set.has('museum')) {
            return t('poi_museum', 'Museum');
        }
        if (set.has('park')) {
            return t('poi_park', 'Park');
        }
        if (set.has('tourist_attraction')) {
            return t('poi_tourist_attraction', 'Cultural attraction');
        }

        return t('poi_generic', 'Point of interest');
    }

    // Wait until Google Maps JS (Places library) is ready
    static ensureGooglePlacesLoaded(timeoutMs = 10000) {
        if (window.google && google.maps && google.maps.places) {
            return Promise.resolve();
        }
        if (SingleApartmentApi._gmapsPromise) return SingleApartmentApi._gmapsPromise;

        SingleApartmentApi._gmapsPromise = new Promise((resolve, reject) => {
            const start = Date.now();
            const check = () => {
                if (window.google && google.maps && google.maps.places) {
                    resolve();
                    return;
                }
                if (Date.now() - start > timeoutMs) {
                    reject(new Error('Google Maps Places library not loaded'));
                    return;
                }
                setTimeout(check, 200);
            };
            check();
        });

        return SingleApartmentApi._gmapsPromise;
    }

    // Call Google Places Nearby Search for our apartment location
    // Call Google Places Nearby Search for our apartment location
    fetchNearbyPlacesFromGoogle(coords) {
        console.log(coords);
        const { lat, lng } = coords;
        const originLat = lat;
        const originLng = lng;

        const placeTypes = [
            'restaurant',
            'cafe',
            'supermarket',
            'gym',
            'museum',
            'park',
            'tourist_attraction'
        ];
        const radius = 3000; // meters

        return SingleApartmentApi.ensureGooglePlacesLoaded().then(() => {
            const location = new google.maps.LatLng(lat, lng);
            const service = new google.maps.places.PlacesService(document.createElement('div'));

            // Helper to wrap nearbySearch in a Promise for a single type
            const requestForType = (type) => new Promise((resolve) => {
                service.nearbySearch(
                    { location, radius, type },
                    (results, status) => {
                        if (status === google.maps.places.PlacesServiceStatus.OK) {
                            resolve(results || []);
                        } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                            resolve([]);
                        } else {
                            // soft-fail: log and resolve empty to not break others
                            console.warn('Places nearbySearch error for type', type, status);
                            resolve([]);
                        }
                    }
                );
            });

            return Promise.all(placeTypes.map(requestForType)).then((chunks) => {
                // Deduplicate by place_id
                const map = new Map();
                chunks.flat().forEach((p) => {
                    if (!p || !p.place_id) return;
                    if (!map.has(p.place_id)) {
                        map.set(p.place_id, p);
                    }
                });

                let places = Array.from(map.values());

                // Filter by rating >= 4.0
                places = places.filter((p) => Number(p.rating || 0) >= 4.0);

                // Normalize and compute distance + walking time
                const normalized = places.map((p) => {
                    const plat = p.geometry && p.geometry.location ? p.geometry.location.lat() : null;
                    const plng = p.geometry && p.geometry.location ? p.geometry.location.lng() : null;
                    let distMeters = null;

                    if (Number.isFinite(plat) && Number.isFinite(plng)) {
                        distMeters = this.computeDistanceMeters(originLat, originLng, plat, plng);
                    }

                    const walkingMinutes = distMeters != null
                        ? Math.max(1, Math.round(distMeters / 80)) // 80m per minute
                        : null;

                    const distanceLabel = walkingMinutes != null
                        ? tr('poi_distance_walk', '{n} min walk', { n: walkingMinutes })
                        : '';

                    const photoUrl = (p.photos && p.photos.length)
                        ? p.photos[0].getUrl({ maxWidth: 400, maxHeight: 300 })
                        : '';

                    const langRaw = (this.lang || '').toLowerCase();
                    const hl = (langRaw.match(/^[a-z]{2}/) || ['en'])[0];

                    const mapsUrl = (() => {
                        if (Number.isFinite(plat) && Number.isFinite(plng)) {
                            // from place_id – pretty link
                            if (p.place_id) {
                                const base = 'https://www.google.com/maps/search/?api=1';
                                const params = new URLSearchParams();
                                params.set('query', `${plat},${plng}`);
                                params.set('query_place_id', p.place_id);
                                params.set('hl', hl);
                                return base + '&' + params.toString();
                            }
                            return `https://www.google.com/maps/search/?api=1&query=${plat},${plng}&hl=${encodeURIComponent(hl)}`;
                        }
                        return '';
                    })();
                    const routeUrl = (Number.isFinite(plat) && Number.isFinite(plng))
                        ? `https://www.google.com/maps/dir/?api=1&origin=${originLat},${originLng}&destination=${plat},${plng}&travelmode=walking`
                        : '';

                    return {
                        name: p.name || '',
                        rating: p.rating || null,
                        category: this.mapPlaceTypesToCategory(p.types || []),
                        distanceMeters: distMeters != null ? distMeters : Number.POSITIVE_INFINITY,
                        walkingMinutes,
                        distance: distanceLabel,
                        image: photoUrl,
                        // mapsUrl: routeUrl
                        mapsUrl
                    };
                });

                // Sort by distance ascending
                normalized.sort((a, b) => a.distanceMeters - b.distanceMeters);

                // Limit to max 9 items
                return normalized.slice(0, 9);
            });
        });
    }


    buildNearbyAttractions($container, _items, $root) {
        if (!$container || !$container.length) return;

        const coords = this.getApartmentCoords();
        if (!coords) {
            // No coordinates => hide section
            if ($container.is("[data-empty-hide]")) $container.prop("hidden", true);
            return;
        }

        // Basic loading state
        $container.children(":not(template)").remove();
        $container
            .prop("hidden", false)
            .append('<div class="p-4 text-sm text-gray-500">' + this.escapeHtml(t('loading_nearby_places', 'Loading nearby places…')) + '</div>');

        const base = ($container.data("base") || "").replace(/\/+$/,"");
        const placeholder = $container.data("placeholder") || (base ? base + "/placeholder.svg" : "/placeholder.svg");

        const resolveUrl = (src) => {
            const s = (src || "").trim();
            if (!s) return placeholder;
            if (/^(https?:)?\/\//.test(s) || s.indexOf("data:") === 0) return s;
            if (s.charAt(0) === "/" && base) return base + s;
            if (base) return base + "/" + s.replace(/^\/+/, "");
            return s;
        };

        const starSvg =
            '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-star w-3 h-3 sm:w-4 sm:h-4 fill-yellow-400 text-yellow-400"><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"></path></svg>';

        const pinSvg =
            '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin w-3 h-3 sm:w-4 sm:h-4 mr-1"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg>';

        // Fetch from Google Places
        this.fetchNearbyPlacesFromGoogle(coords)
            .then((items) => {
                console.log(items);
                // clear loader
                $container.children(":not(template)").remove();

                // hide section if fewer than 6 results
                if (!Array.isArray(items) || items.length < 6) {
                    if ($container.is("[data-empty-hide]")) $container.prop("hidden", true);
                    return;
                }

                $container.prop("hidden", false);

                let html = "";
                for (let i = 0; i < items.length; i++) {
                    const it = items[i] || {};
                    const img = resolveUrl(it.image);
                    const name = it.name ? this.escapeHtml(it.name) : "";
                    const category = it.category ? this.escapeHtml(it.category) : "";
                    const distance = it.distance ? this.escapeHtml(it.distance) : "";
                    const mapsUrl = it.mapsUrl ? this.escapeHtml(it.mapsUrl) : "";

                    let ratingStr = "";
                    if (it.rating != null && isFinite(Number(it.rating))) {
                        const r = Math.round(Number(it.rating) * 10) / 10;
                        ratingStr = String(r).replace(/\.0$/, "");
                    }

                    const wrapperTag = mapsUrl ? "a" : "div";
                    const wrapperAttrs = mapsUrl
                        ? `href="${mapsUrl}" target="_blank" rel="noopener noreferrer"`
                        : "";

                    html += `
                      <${wrapperTag}
                        class="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                        ${wrapperAttrs}
                      >
                        <div class="relative">
                          <img
                            src="${this.escapeHtml(img)}"
                            alt="${name}"
                            class="w-full h-24 sm:h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                          ${ratingStr ? `
                            <div class="absolute top-2 right-2 sm:top-3 sm:right-3 bg-white/95 backdrop-blur-sm rounded-full px-2 py-1 sm:px-3 sm:py-1 text-xs sm:text-sm font-medium shadow-sm">
                              <div class="flex items-center space-x-1">
                                ${starSvg}
                                <span class="text-gray-800">${ratingStr}</span>
                              </div>
                            </div>
                          ` : ''}
                        </div>
                
                        <div class="p-3 sm:p-4">
                          <div class="flex items-start justify-between mb-2">
                            <h3 class="font-semibold text-basel-gray text-sm sm:text-base">${name}</h3>
                          </div>
                
                          <div class="flex flex-wrap gap-1 flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                            ${category ? `
                              <span class="bg-basel-blue/10 text-basel-blue px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-medium w-fit">
                                ${category}
                              </span>
                            ` : ''}
                
                            ${distance ? `
                              <div class="flex items-center text-gray-600 text-xs sm:text-sm">
                                ${pinSvg}
                                <span class="font-medium">${distance}</span>
                              </div>
                            ` : ''}
                          </div>
                        </div>
                      </${wrapperTag}>
                    `;
                }

                $container.children(":not(template)").remove();
                $container.append(html);
                if (html) $container.closest('.bind-container').addClass('-visible');
            })
            .catch((err) => {
                console.error('Failed to load nearby attractions', err);
                $container.children(":not(template)").remove();
                if ($container.is("[data-empty-hide]")) $container.prop("hidden", true);
            });
    };

    buildGallery($container, images, $root, ctx) {
        if (!$container || !$container.length) return;

        if (!$.isArray(images) || !images.length) {
            if ($container.is("[data-empty-hide]")) $container.prop("hidden", true);
            return;
        }
        $container.prop("hidden", false);

        var placeholder = (typeof window.BL_PHOTO_PLACEHOLDER === "string" && window.BL_PHOTO_PLACEHOLDER)
            ? window.BL_PHOTO_PLACEHOLDER
            : "/placeholder.svg";

        function safeUrl(u){ u = (u || "").trim(); return u ? u : placeholder; }

        var normalized = images.map(safeUrl);
        if (Array.isArray(window.images)) {
            window.images.splice(0, window.images.length, ...normalized);
        } else {
            window.images = normalized.slice();
        }

        var modal = document.getElementById("gallery-modal");
        if (modal && !modal.classList.contains("hidden")) {
            if (typeof renderThumbnails === "function") renderThumbnails();
            if (typeof updateModalImage === "function") updateModalImage();
        }

        var title = (ctx && ctx.title) || (this.apartmentData && this.apartmentData.title) || t('apartment_photo', 'Apartment photo');
        var mainSrc = safeUrl(images[0]);

        // Desktop thumbs: show up to 4 (1..4)
        var total = images.length;
        var desktopCount = Math.min(Math.max(total - 1, 0), 4);

        var desktopHtml = "";
        for (var i = 1; i <= desktopCount; i++) {
            var src = safeUrl(images[i]);
            var isLast = (i === 4) && (total - 5 > 0);   // if there are more than 5 photos
            var more = total - 5;

            desktopHtml += ''
                + '<div class="hidden md:block overflow-hidden rounded-lg cursor-pointer relative" onclick="openGallery(' + i + ')">'
                +   '<img src="' + this.escapeHtml(src) + '" alt="' + this.escapeHtml(title) + '"'
                +        ' class="w-full h-full object-cover hover:scale-105 transition-transform duration-300" loading="lazy" />'
                +   (isLast
                    ? '<div class="absolute inset-0 bg-black/40 flex items-center justify-center">'
                    + '<span class="text-white font-medium text-sm sm:text-base">+' + more + '</span>'
                    + '</div>'
                    : '')
                + '</div>';
        }

        // Mobile thumbs: show all from 1..end in a horizontal scroll
        var mobileHtml = "";
        for (var m = 1; m < total; m++) {
            var msrc = safeUrl(images[m]);
            mobileHtml += ''
                + '<div class="flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden cursor-pointer" onclick="openGallery(' + m + ')">'
                +   '<img src="' + this.escapeHtml(msrc) + '"'
                +        ' class="w-full h-full object-cover hover:scale-105 transition-transform duration-300" loading="lazy" />'
                + '</div>';
        }

        var html = `
          <!-- Main Image -->
          <div class="md:col-span-2 md:row-span-2 relative overflow-hidden rounded-lg cursor-pointer">
            <img
              src="${this.escapeHtml(mainSrc)}"
              alt="${this.escapeHtml(title)}"
              class="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              onclick="openGallery(0)"
            />
            <div class="flex items-center justify-between absolute bottom-4 right-4 gap-2">
              <button
                class="bg-white/90 hover:bg-white text-xs sm:text-sm px-3 py-1 rounded shadow flex items-center gap-2"
                onclick="openGallery(0)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-grid3x3 w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2">
                  <rect width="18" height="18" x="3" y="3" rx="2"></rect>
                  <path d="M3 9h18"></path>
                  <path d="M3 15h18"></path>
                  <path d="M9 3v18"></path>
                  <path d="M15 3v18"></path>
                </svg>
                <span class="hidden sm:inline">${t('view_all_photos', 'View All Photos')}</span>
                <span class="sm:hidden">${t('gallery', 'Gallery')}</span>
              </button>
        
              ${BL_AJAX.floorplan_link ? `
              <a href="${BL_AJAX.floorplan_link}" target="_blank"
                class="bg-white/90 hover:bg-white text-xs sm:text-sm px-3 py-1 rounded shadow flex items-center gap-2">
                <span class="hidden sm:inline">${t('view_floorplan', 'View floorplan')}</span>
                <span class="sm:hidden">${t('floorplan', 'Floorplan')}</span>
              </a>` : ``}
        
              ${BL_AJAX.tour_link ? `
              <button
                class="bg-white/90 hover:bg-white text-xs sm:text-sm px-3 py-1 rounded shadow flex items-center gap-2"
                onclick="document.getElementById('tour3d-modal').classList.remove('hidden')"
              >
                <span class="hidden sm:inline">${t('take_a_3d_tour', 'Take a 3D Tour')}</span>
                <span class="sm:hidden">${t('3d_tour', '3D Tour')}</span>
              </button>` : ``}
            </div>
          </div>
        
          ${total > 1 ? `
            <!-- Mobile strip -->
            <div class="md:hidden col-span-1">
              <div class="flex gap-2 overflow-x-auto pb-2">${mobileHtml}</div>
            </div>
          ` : ``}
        
          <!-- Desktop thumbs -->
          ${desktopHtml}
        `;

        // replace content
        $container.children(":not(template)").remove();
        $container.append(html);
        if(html) $container.closest('.bind-container').addClass('-visible');
    };

    parseNum(x) { const n = Number(x); return Number.isFinite(n) ? n : null; }

    extrasUnitsByStay(freq, nights) {
        const f = String(freq || '').toLowerCase();
        if (f === 'weekly')  return Math.max(1, Math.ceil(nights / 7));
        if (f === 'monthly') return Math.max(1, Math.ceil(nights / 30));
        // 'one-time' or unknown — once
        return 1;
    }

    computeMonthlyGross(pricing, currency, locale) {
        const p   = pricing || {};
        const netPerNight = Number(p.pricePerNight || 0);
        const disc = Number(p.longTermDiscountRate || 0); // 0.15 = -15%
        const vat  = Number(p.vatRate || 0);

        // if a ready monthly rate comes — use it; otherwise: 30 * nightly * (1 - disc)
        const monthlyNet = Number(p.monthlyRate || (30 * netPerNight * (1 - disc)));
        const monthlyGross = monthlyNet * (1 + vat);
        return this.currencyFormat(monthlyGross, currency, locale);
    }

    addDays(d, days){ const out=new Date(d); out.setDate(out.getDate()+days); return out; }
    parseISO(s){ if(!s) return null; const m=String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/); if(!m) return null; return new Date(Date.UTC(+m[1], +m[2]-1, +m[3])); }
    formatISO(d){ return d ? d.toISOString().slice(0,10) : ""; }

    renderGuestsSelect($container) {
        const $sel = $container.find('[data-role="guests"]');
        if (!$sel.length) return;

        // maxGuests from backend or 4 by default
        const raw = Number(this.apartmentData?.specifications?.maxOccupancy);
        const cap = Number.isFinite(raw) && raw > 0 ? raw : 4;

        // selection: from URL -> from internal state -> 1
        let mgFromUrl = this.getUrlParamInt('guests') || this.getUrlParamInt('maxGuests');
        let selected = mgFromUrl || this.guests || 1;

        // clamp to [1..cap]
        selected = Math.min(Math.max(selected, 1), cap);

        // build options
        let opts = '';
        for (let i = 1; i <= cap; i++) {
            const lbl = `${i} ${i === 1 ? t('guest','Guest') : t('guests','Guests')}`;
            opts += `<option value="${i}" ${i === selected ? 'selected' : ''}>${lbl}</option>`;
        }

        // if select2 was already attached — destroy before updating options
        if ($sel.hasClass('select2-hidden-accessible')) $sel.select2('destroy');

        $sel.html(opts);
        this.guests = selected;

        // if URL had more than allowed — immediately fix the URL
        if (mgFromUrl && mgFromUrl !== selected) {
            this.updateUrlParams({ guests: selected });
        }
    }

    // ------------- MOBILE PRICE BAR (form + 2-way sync) -------------
    buildMobileBar($mobile, cfg) {
        if (!$mobile || !$mobile.length) return;

        // base values for form attributes
        const base   = this.apartmentDataFromResh ? this.extractReshPricing() : this.getMicroserviceBaseline();
        const rate   = base.priceFromType || 'DAILY';
        const minStayDays = (cfg && cfg.minStayDays) || base.minStayDays || (rate === 'MONTHLY' ? 28 : 1);

        const html = `
    <div class="js-pricing-form-mobile"
     data-min-stay="${minStayDays}" data-rate-type="${rate}">
      <div class="space-y-3">
        <div class="grid grid-cols-3 gap-2">
          <input type="text" name="m_dateFrom" placeholder="${t('arrival','Arrival Date')}"
                 class="check-input w-full h-9 border rounded-md px-3 text-xs"
                 data-role="checkin" value="${this.dates.checkIn || ''}"
                 autocomplete="off" required>

          <input type="text" name="m_dateTo" placeholder="${t('departure','Departure Date')}"
                 class="check-input w-full h-9 border rounded-md px-3 text-xs"
                 data-role="checkout" value="${this.dates.checkOut || ''}"
                 autocomplete="off" required>

          <div class="select-box">
            <select name="m_guests" data-role="guests"
                    class="w-full rounded-md border px-3 py-2 h-9 text-xs" required>
              <!-- options will be generated below -->
            </select>
          </div>
        </div>
        
        ${this.getUnsureCheckoutCheckboxHtml()}

        <div class="flex items-center justify-between">
          <div class="flex-1">
            <div class="text-lg font-bold text-basel-gray">
              <span class="js-price-amount">CHF —</span>
              <span class="text-sm font-normal text-gray-600 ml-1 js-price-suffix">/${t('night', 'night')}</span>
            </div>
            <div class="text-xs text-gray-600 js-mobile-summary" aria-live="polite"></div>
          </div>

          <div class="js-cta"></div>
        </div>
        <div class="js-breakdown-wrapper"></div>
      </div>
    </div>
  `;

        $mobile.html(html);

        // 1) the same inits as on desktop
        this.renderGuestsSelect($mobile);     // creates <option> and sets this.guests
        this.initSelects($mobile);            // select2
        this.initDatepickers($mobile, cfg || { minStayDays, rateType: rate }); // flatpickr

        // 2) two-way mirroring (mobile form -> desktop)
        const $desk = $('#pricing-box');
        const $dCI  = $desk.find('[data-role="checkin"]');
        const $dCO  = $desk.find('[data-role="checkout"]');
        const $dG   = $desk.find('[data-role="guests"]');

        const $mCI  = $mobile.find('[data-role="checkin"]');
        const $mCO  = $mobile.find('[data-role="checkout"]');
        const $mG   = $mobile.find('[data-role="guests"]');
        const $mUnsureCheckout = $mobile.find('[data-role="unsure-checkout"]');

        // всередині buildMobileBar, поруч із mirror/setOnTarget
        this._mirrorTimer && clearTimeout(this._mirrorTimer);
        this._mirrorTimer = null;

// один контрольований тригер десктопного onChange (через checkout)
        const triggerDesktopRecalcOnce = () => {
            if (this._mirrorTimer) clearTimeout(this._mirrorTimer);
            this._mirrorTimer = setTimeout(() => {
                this._mirrorTimer = null;

                const coEl = $dCO[0];
                const fp = coEl && coEl._flatpickr;
                if (fp) {
                    const v = $dCO.val();
                    if (v) fp.setDate(v, true);   // ← один офіційний onChange на десктопі
                } else {
                    $dCO.trigger('change');       // фолбек без flatpickr
                }
            }, 120); // 100–200 мс достатньо, щоб "склеїти" CI+CO
        };

        const setOnTarget = (to, v, isDate = false) => {
            const el = to && to[0];
            const fp = el && el._flatpickr;
            if (fp) {
                fp.setDate(v || null, false);
                if (isDate) triggerDesktopRecalcOnce();
            } else {
                // для select2 нормально тригерити одразу
                to.val(v || '').trigger('change');
            }
        };

        const mirror = (from, to, isDate = false) => {
            from.off('.mobileMirror').on('change.mobileMirror', () => {
                if (this._syncing) return;
                const v = from.val();
                const isCheckin = from.is('[data-role="checkin"]');
                const isCheckout = from.is('[data-role="checkout"]');

                if (isCheckin && this.hasUnsureCheckoutParam()) {
                    setOnTarget($mCO, '', false);
                    setOnTarget($dCO, '', false);
                    this.dates.checkOut = '';
                    this.syncUnsureCheckoutCheckboxes(true);
                }

                // оновлюємо десктоп тихо; для дат — одного тригера потім достатньо
                setOnTarget(to, v, isDate);

                // URL синхронізуємо як і раніше
                if (from.is('[data-role="checkin"]')) {
                    this.updateUrlParams({ dateFrom: v || null });
                }

                if (from.is('[data-role="checkout"]')) {
                    if (this.hasUnsureCheckoutParam()) {
                        this.removeUnsureCheckoutMode();
                    }

                    this.updateUrlParams({ dateTo: v || null });
                }
            });
        };
        mirror($mCI, $dCI, true);
        mirror($mCO, $dCO, true);
        mirror($mG,  $dG,  false);

        $mUnsureCheckout.off('change.mobileUnsureCheckout').on('change.mobileUnsureCheckout', () => {
            const checked = $mUnsureCheckout.is(':checked');

            this.syncUnsureCheckoutCheckboxes(checked);

            if (checked) {
                const arrivalDate = $mCI.val() || $dCI.val() || '';
                this.applyUnsureCheckoutMode(arrivalDate);
            } else {
                this.removeUnsureCheckoutMode();
            }

            const dCiEl = $dCI[0];
            const fp = dCiEl && dCiEl._flatpickr;

            if (fp) {
                fp.setDate($dCI.val() || null, true);
            } else {
                $dCI.trigger('change');
            }
        });

        const $wrap = $mobile.find('.js-pricing-form-mobile');
        const host = ($el) => $el.closest('.select-box').length ? $el.closest('.select-box') : $el.parent();
        const setErr = ($el, bad) => {
            host($el).toggleClass('error', !!bad);
            const $s2 = $el.next('.select2');
            if ($s2.length) $s2.find('.select2-selection').toggleClass('error', !!bad);
        };

        $mCI.on('input change', () => setErr($mCI, false));
        $mCO.on('input change', () => setErr($mCO, false));
        $mG .on('change',       () => setErr($mG,  false));

        // 3) initial mobile box update
        this.updateMobileBar($mobile);

        $mobile.data('inited', true);
    }

    isValidIsoDate(str) {
        return !!(str && /^\d{4}-\d{2}-\d{2}$/.test(str) && this.parseISO(str));
    }

    validateMobileVisualOnly($wrap) {
        let ok = true;
        const $ci = $wrap.find('[data-role="checkin"]');
        const $co = $wrap.find('[data-role="checkout"]');
        const $g  = $wrap.find('[data-role="guests"]');

        const clear = ($el) => $el.parent().removeClass('error');
        const mark  = ($el) => { $el.parent().addClass('error'); ok = false; };

        clear($ci); clear($co); clear($g);

        const ci = $ci.val();
        const co = this.getEffectiveCheckoutDate(ci, $co.val());
        const gv = parseInt($g.val(), 10);
        const minStay = Number($wrap.data('minStay')) || 1;

        if (!this.isValidIsoDate(ci)) mark($ci);
        if (!this.isValidIsoDate(co)) mark($co);

        if (ok) {
            const n = this.computeNights(ci, co);
            if (n <= 0 || n < minStay) mark($co);
        }

        if (!Number.isFinite(gv) || gv < 1) mark($g);

        return ok;
    }

    updateMobileBar($mobile = $('#pricing-box-mobile')) {
        if (!$mobile || !$mobile.length) return;

        const currency = 'CHF', locale = 'de-CH';
        const base = this.apartmentDataFromResh ? this.extractReshPricing()
            : this.getMicroserviceBaseline();

        // header as before
        $mobile.find('.js-price-amount').text(this.currencyFormat(base.priceFromGross, currency, locale));
        $mobile.find('.js-price-suffix').text(base.priceFromType === 'PER_MONTH' ? t('per_month','per month') : t('per_night','per night'));

        // inputs sync (keep as you had)
        const setSilently = ($el, val) => {
            const fp = $el[0] && $el[0]._flatpickr;
            if (fp) { val ? fp.setDate(val, false) : fp.clear(); } else { $el.val(val || ''); }
        };
        this._syncing = true;
        setSilently($mobile.find('[data-role="checkin"]'),  this.dates.checkIn || '');
        setSilently($mobile.find('[data-role="checkout"]'), this.dates.checkOut || '');
        const $mG = $mobile.find('[data-role="guests"]');
        if ($mG.length) {
            const val = String(this.guests || 1);
            if ($mG.val() !== val) { $mG.val(val); if ($mG.hasClass('select2-hidden-accessible')) $mG.trigger('change.select2'); }
        }
        this._syncing = false;

        // 🔒 show summary ONLY from API (RESH) and only for DAILY
        const reshReady = !!(this.apartmentDataFromResh
            && this._lastFetchedDates
            && this._lastFetchedDates.dateFrom === this.dates.checkIn
            && this._lastFetchedDates.dateTo   === this.dates.checkOut);

        let summary = '';
        if (reshReady && base.priceFromType !== 'PER_MONTH') {
            const apiTotal  = Number(base.totalsGross) || 0;  // total for the stay from API
            const apiNights = Number(base.nights)      || 0;  // nights from API
            if (apiTotal > 0 && apiNights > 0) {
                const extras = this.getExtrasTotal(apiNights, this.guests) || 0;
                const grand  = apiTotal + extras;

                const tmplKey = apiNights === 1
                    ? 'mobile_total_summary_one'
                    : 'mobile_total_summary_many';

                summary = tr(
                    tmplKey,
                    apiNights === 1
                        ? 'Total: {amount} for {n} night'
                        : 'Total: {amount} for {n} nights',
                    {
                        amount: this.currencyFormat(grand, currency, locale),
                        n: apiNights
                    }
                );
            }
        }
        $mobile.find('.js-mobile-summary').text(summary);

        // mirror CTA with desktop logic
        const $cta = $mobile.find('.js-cta');
        $cta.html(
            base.priceFromType === 'PER_MONTH'
                ? `<button class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-primary-foreground h-10 bg-basel-blue hover:bg-basel-blue-dark px-6 py-2 text-sm font-medium">${t('send_inquiry','Send an Inquiry')}</button>`
                : `<button class="js-book-now inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-primary-foreground h-10 bg-basel-blue hover:bg-basel-blue-dark px-6 py-2 text-sm font-medium">${t('book_now','Book Now')}</button>`
        );
    }

// helper: simultaneous loader for both CTAs
    setAllCtasLoading(isLoading, label = t('checking','Checking...')) {
        this.setCtaLoading($('#pricing-box'),        isLoading, label);
        this.setCtaLoading($('#pricing-box-mobile'), isLoading, label);
    }
// --------------------------------------------


    buildPricingBox($container, data, $root, opts = {}) {
        if (!$container || !$container.length) return;

        const currency = "CHF";
        const locale   = "de-CH";

        const base = this.apartmentDataFromResh ? this.extractReshPricing() : this.getMicroserviceBaseline();

        const rateType    = base.priceFromType || 'DAILY';
        const minStayDays = base.minStayDays;
        const headerNow   = this.currencyFormat(base.priceFromGross, currency, locale);
        const suffix      = rateType === 'MONTHLY' ? t('per_month','per month') : t('per_night','per night');

        const html = `
      <div class="rounded-lg border bg-card text-card-foreground shadow-lg">
        <div class="flex flex-col space-y-1.5 p-6">
          <div class="flex items-center justify-between text-2xl font-semibold leading-none tracking-tight">
            <div><span class="text-2xl font-bold js-price-amount">${headerNow}</span></div>
            <span class="text-sm text-gray-600 js-price-suffix">${suffix}</span>
          </div>
        </div>
        <div class="p-6 pt-0 space-y-4" id="pricing-box-form-wrap">
          <form class="js-pricing-form" novalidate
                data-min-stay="${minStayDays}"
                data-rate-type="${rateType}"
                data-apartment-id="${this.apartmentId}">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input type="text" name="dateFrom" placeholder="${t('arrival','Arrival Date')}"
                     class="w-full h-10 sm:h-9 border rounded-md px-3 text-sm sm:text-base check-input"
                     data-role="checkin" value="${this.dates.checkIn || ''}" autocomplete="off" required>
              <input type="text" name="dateTo" placeholder="${t('departure','Departure Date')}"
                     class="w-full h-10 sm:h-9 border rounded-md px-3 text-sm sm:text-base check-input"
                     data-role="checkout" value="${this.dates.checkOut || ''}" autocomplete="off" required>
            </div>
            <div class="select-box">
              <select name="guests" data-role="guests" class="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm h-10 sm:h-9" required></select>
            </div>
            
            ${this.getUnsureCheckoutCheckboxHtml()}
          </form>
          <div class="js-long-info"></div>
          <div class="js-breakdown-wrapper"></div>
          <div class="js-cta"></div>
        </div>
      </div>
    `;

        $container.html(html);
        this.renderGuestsSelect($container);
        this.applyParamsFromUrl();
        this.dates.checkIn  = $container.find('[data-role="checkin"]').val()  || '';
        this.dates.checkOut = $container.find('[data-role="checkout"]').val() || '';

        const autoFetchOnInit = opts.autoFetchOnInit !== false; // default: true

        this.buildMobileBar($('#pricing-box-mobile'), {
            rateType,
            nightlyPrice: rateType === 'DAILY'   ? base.priceFromGross : 0,
            monthlyPrice: rateType === 'MONTHLY' ? base.priceFromGross : 0,
            minStayDays
        });


        this.initPricingInteractions($container, currency, locale, {
            rateType,
            nightlyPrice: rateType === 'DAILY'   ? base.priceFromGross : 0,
            monthlyPrice: rateType === 'MONTHLY' ? base.priceFromGross : 0,
            minStayDays
        }, { autoFetchOnInit });
        this.initSelects($container);

        this.setupFormValidation();
    }

    setupFormValidation() {
        // custom rules
        if (jQuery.validator) {
            $.validator.addMethod('isoDate', v => !v || /^\d{4}-\d{2}-\d{2}$/.test(v), 'Invalid date.');
            $.validator.addMethod('checkDateOrder', function (v, el) {
                const $f = $(el.form);
                const ci = $f.find('[name="dateFrom"]').val();
                const co = $f.find('[name="dateTo"]').val();
                if (!ci || !co) return true;
                const n = singleApartmentApi.computeNights(ci, co);
                return n > 0;
            }, 'End date must be after start date.');
            $.validator.addMethod('minNights', function (v, el) {
                const $f = $(el.form);
                const ci = $f.find('[name="dateFrom"]').val();
                const co = $f.find('[name="dateTo"]').val();
                const minStay = Number($f.data('minStay')) || 1;
                if (!ci || !co) return true;
                const n = singleApartmentApi.computeNights(ci, co);
                return n >= minStay;
            }, 'Stay is too short.');
        }

        $(SELECTORS.FORM).validate({
            focusInvalid: false,
            rules: {
                dateFrom: { required: true, isoDate: true },
                dateTo:   { required: true, isoDate: true, checkDateOrder: true, minNights: true },
                guests:   { required: true, digits: true, min: 1 }
            },
            errorPlacement: function(){ return false; },
            highlight: function (el) { $(el).parent().addClass('error'); },
            unhighlight: function (el) { $(el).parent().removeClass('error'); }
        });
    }

    validateInputs() {
        const form = $(SELECTORS.FORM);
        if (!form.length) return false;

        let allValid = true;
        const dateFrom = form.find('[name="dateFrom"]').val() || '';
        const effectiveDateTo = this.getEffectiveCheckoutDate(dateFrom, form.find('[name="dateTo"]').val() || '');
        const allowEmptyCheckout = this.hasUnsureCheckoutParam() && this.isValidIsoDate(dateFrom) && this.isValidIsoDate(effectiveDateTo);

        form.find(':input').each((_, el) => {
            const $el = $(el);
            const $p = $el.parent();

            if ($el.attr('name') === 'dateTo' && allowEmptyCheckout) {
                $p.removeClass('error');
                return;
            }

            form.validate().element(el);
            if (!el.checkValidity?.() || $p.hasClass('error')) allValid = false;
        });

        form.find('select').each(function () {
            const $p = $(this).parent();
            form.validate().element(this);
            if (!this.checkValidity?.() || $p.hasClass('error')) allValid = false;
        });

        return allValid;
    }

    async onBookNowClick(e) {
        e.preventDefault();

        // якщо тиснули "Book now" у мобільному блоці — лише візуальна перевірка й тихе дзеркалення
        if ($(e.currentTarget).closest('#pricing-box-mobile').length) {
            const $mobileWrap = $('#pricing-box-mobile .js-pricing-form-mobile');
            if (!$mobileWrap.length) return;

            const host = ($el) => $el.closest('.select-box').length ? $el.closest('.select-box') : $el.parent();
            const addErr = ($el) => {
                host($el).addClass('error');
                const $s2 = $el.next('.select2'); // для select2
                if ($s2.length) $s2.find('.select2-selection').addClass('error');
            };

            const $ci = $mobileWrap.find('[data-role="checkin"]');
            const $co = $mobileWrap.find('[data-role="checkout"]');
            const $g  = $mobileWrap.find('[data-role="guests"]');

            const effectiveMobileCheckout = this.getEffectiveCheckoutDate($ci.val() || '', $co.val() || '');

            let hasEmpty = false;
            if (!$ci.val()) { addErr($ci); hasEmpty = true; }
            if (!effectiveMobileCheckout) { addErr($co); hasEmpty = true; }
            if (!$g.val())  { addErr($g);  hasEmpty = true; }

            if (hasEmpty) { e.preventDefault(); return; }

            // 1) візуальна перевірка (без будь-яких API/перерахунків)
            if (!this.validateMobileVisualOnly($mobileWrap)) {
                e.preventDefault();
                return;
            }

            // 2) тихо перенести значення в десктопні поля (без onChange)
            const $desk = $('#pricing-box');
            const $dCI  = $desk.find('[data-role="checkin"]');
            const $dCO  = $desk.find('[data-role="checkout"]');
            const $dG   = $desk.find('[data-role="guests"]');

            const setQuiet = (to, val) => {
                const el = to[0];
                const fp = el && el._flatpickr;
                if (fp) fp.setDate(val || null, false);
                else to.val(val || '');
            };

            setQuiet($dCI, $mobileWrap.find('[data-role="checkin"]').val());
            setQuiet($dCO, $mobileWrap.find('[data-role="checkout"]').val());
            $dG.val($mobileWrap.find('[data-role="guests"]').val()).trigger('change');

        }

        if (!this.validateInputs()) return;

        const $box  = $('#pricing-box');
        const $errorContainer = $(SELECTORS.ERROR_CONTAINER);
        const $form = $(SELECTORS.FORM);
        const dateFrom = $form.find('[name="dateFrom"]').val();
        const dateTo   = this.getEffectiveCheckoutDate(dateFrom, $form.find('[name="dateTo"]').val());
        const guests   = parseInt($form.find('[name="guests"]').val(), 10) || 1;

        const key = `${dateFrom}|${dateTo}|${guests}`;
        this._availKey = key;

        this.setAllCtasLoading(true, t('checking','Checking...'));

        const similarIds = this.getSimilarApartmentIds();

        this.resetSimilarAvailabilityUI();

        this.fetchUnitAvailability({ dateFrom, dateTo, guests, similarApartmentIds: similarIds, _key: key })
            .done((res) => {
                const payload = res?.data || {};
                const ok = !!(res && res.success && (payload.available ?? true));
                if (!ok) {
                    this.showUnavailableUI($errorContainer, payload.message || t('apartment_is_not_available', 'The apartment is not available on these dates.'));
                    this.renderSimilarAnchor($errorContainer);
                    this.setAllCtasLoading(false);
                    return;
                }

                // 1) form booking data (without URL)
                const extras = Object.keys(this.addedExtraServices || {}).sort();
                const unitTypeType = (this.apartmentData?.specifications?.bedroomType || '').toString();
                const maxGuests = (this.apartmentData?.specifications?.maxOccupancy || '').toString();
                const hiddenExtras = Array.from(this.getHiddenExtrasSlugsSet() || []).sort();

                const checkoutPayload = {
                    apartmentId: this.apartmentId,
                    pageId: this.pageId,
                    dateFrom, dateTo, guests, unitTypeType, maxGuests,
                    extras, hiddenExtras
                };

                // 2) ask backend to create a short-lived token
                this.createCheckoutToken(checkoutPayload)
                    .done((r) => {
                        if (!r?.success || !r?.data?.token) {
                            this.showUnavailableUI($errorContainer, t('could_not_create_checkout_token', 'Could not create checkout token. Please try again.'));
                            return;
                        }
                        const token = r.data.token;
                        const redirectUrl = payload.redirect || '/short-term-booking';
                        const finalUrl = this.appendParam(redirectUrl, 't', token);

                        // 3) redirect with token
                        window.location.assign(finalUrl);
                    })
                    .fail(() => {
                        this.showUnavailableUI($errorContainer,  t('could_not_create_checkout_token', 'Could not create checkout token. Please try again.'));
                    })
                    .always(() => {
                        this.setAllCtasLoading(false);
                    });
            })
            .fail((jq) => {
                const msg = jq?.responseJSON?.data?.message || t('apartment_is_not_available', 'The apartment is not available on these dates.');
                this.showUnavailableUI($errorContainer, msg);
                this.renderSimilarAnchor($errorContainer);
                this.setAllCtasLoading(false);
            });
    }

    initPricingInteractions($container, currency, locale, cfg, options = {}){

        const $ci = $container.find('[data-role="checkin"]');
        const $co = $container.find('[data-role="checkout"]');
        const $unsureCheckout = $container.find('[data-role="unsure-checkout"]');

        const $errorContainer = $(SELECTORS.ERROR_CONTAINER);

        if (!$ci.length || !$co.length) return;

        // -------- initial dates from URL with validation (keep standalone valid dateTo) --------
        const urlCI = this.getUrlParamDate('dateFrom') || null;
        let urlCO = this.getUrlParamDate('dateTo') || null;

        let isUnsureCheckoutInitial = this.hasUnsureCheckoutParam();

        // If unsureCheckout exists without arrival date, remove only unsureCheckout.
        if (isUnsureCheckoutInitial && !urlCI) {
            this.updateUrlParams({ unsureCheckout: null });
            this.unsureCheckoutActive = false;
            isUnsureCheckoutInitial = false;
        }

        // unsureCheckout has priority over dateTo.
        // If both exist, remove dateTo from URL and use computed date internally.
        if (isUnsureCheckoutInitial && urlCO) {
            this.updateUrlParams({ dateTo: null });
            urlCO = null;
        }

        if (isUnsureCheckoutInitial && urlCI) {
            urlCO = this.getComputedUnsureCheckoutDate(urlCI) || null;
        }

        this.unsureCheckoutActive = isUnsureCheckoutInitial;
        this.syncUnsureCheckoutCheckboxes(isUnsureCheckoutInitial);
        this.renderUnsureCheckoutInfobox();

        const minStayDays = Number(cfg?.minStayDays) || 1;
        // midnight today (local)
        const today = (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();

        const parseISO = (s) => this.parseISO(s);
        const addDaysUTC = (d, n) => { const x = new Date(d.getTime()); x.setUTCDate(x.getUTCDate() + n); return x; };

        let hadUrlDates = false;

        let keepCI = false;
        let keepCO = false;

        const ci = urlCI ? parseISO(urlCI) : null;
        const co = urlCO ? parseISO(urlCO) : null;

        // CI is valid only if not before today
        if (ci && ci >= today) keepCI = true;

        // CO rules:
        // - If CI is valid: CO >= (CI + minStayDays)
        // - If CI is missing/invalid: allow standalone CO if CO >= (today + minStayDays)
        if (co) {
            if (keepCI) {
                const minCOWithCI = addDaysUTC(ci, minStayDays);
                if (co >= minCOWithCI) keepCO = true;
            } else {
                const todayUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
                const minCOGlobal = addDaysUTC(todayUTC, minStayDays);
                if (co >= minCOGlobal) keepCO = true;
            }
        }

        // Apply to inputs + clean URL only for invalid pieces.
        // In "check-out unknown" mode, keep the visible checkout field empty,
        // but keep using the computed checkout date internally for availability.
        if (keepCI) {
            $ci.val(urlCI);
        } else {
            this.updateUrlParams({ dateFrom: null });
            $ci.val('');
        }

        if (keepCO && !isUnsureCheckoutInitial) {
            $co.val(urlCO);
        } else {
            this.updateUrlParams({ dateTo: null });
            $co.val('');
        }

        // Auto-availability check runs when both effective dates are valid
        hadUrlDates = (keepCI && keepCO);

        this.dates.checkIn  = $ci.val() || '';
        this.dates.checkOut = $co.val() || '';
        // --------------------------------------------------------------------------------------

        this.initDatepickers($container, cfg);

        this.syncUnsureCheckoutCheckboxes(isUnsureCheckoutInitial);
        this.toggleUnsureCheckoutUI(isUnsureCheckoutInitial);

        // ---- helpers ----
        function sameDates(a, b) {
            return !!(a && b && a.dateFrom === this.dates.checkIn && a.dateTo === this.dates.checkOut);
        }

        const hasReshForCurrentDates = () => {
            const effectiveDateTo = this.getEffectiveCheckoutDate(this.dates.checkIn, this.dates.checkOut);

            return !!(this.apartmentDataFromResh &&
                this._lastFetchedDates &&
                this._lastFetchedDates.dateFrom === this.dates.checkIn &&
                this._lastFetchedDates.dateTo   === effectiveDateTo);
        };

        // RENDER (only show breakdown with RESH)
        const renderFromResh = () => {
            const useResh = !!this.apartmentDataFromResh;
            const { priceFromGrossNet, priceFromGross, priceFromType, vatAmount, totalsGrossNet, totalsGross, nights } =
                useResh ? this.extractReshPricing() : this.getMicroserviceBaseline();

            const newCat = this.getCurrentStayCategoryFromType(priceFromType);
            if (this._currentStayCategory !== newCat) {
                this._currentStayCategory = newCat;
                this.rerenderExtrasForCategory(newCat); // re-render list and drop invalid selections+URL
            }

            const effectiveDateTo = this.getEffectiveCheckoutDate(this.dates.checkIn, this.dates.checkOut);
            const hasDates = !!(this.dates.checkIn && effectiveDateTo);
            const $amount = $container.find('.js-price-amount');
            const $suffix = $container.find('.js-price-suffix');
            const $info   = $container.find('.js-long-info');
            const $break  = $container.find('.js-breakdown-wrapper');
            const $cta    = $container.find('.js-cta');

            const headerNum = priceFromGross;
            $amount.text(this.currencyFormat(headerNum, currency, locale));
            $suffix.text(priceFromType === 'MONTHLY' ? t('per_month','per month') : t('per_night','per night'));

            const reshReady = hasReshForCurrentDates();
            // 🔽 Change CTA here depending on type
            if (!hasDates || !reshReady) {
                $info.empty();
                $errorContainer.empty();

                const isMonthly = (priceFromType === 'MONTHLY');
                $cta.html(
                    isMonthly
                        ? `<button class="js-book-now w-full bg-basel-blue hover:bg-basel-blue-dark h-11 sm:h-10 text-white text-sm sm:text-base font-medium rounded-md">${t('send_inquiry','Send an Inquiry')}</button>`
                        : `<button class="js-book-now w-full bg-basel-blue hover:bg-basel-blue-dark h-11 sm:h-10 text-white text-sm sm:text-base font-medium rounded-md">${t('book_now','Book Now')}</button>`
                );
                return;
            }

            if (priceFromType === 'MONTHLY') {
                // 🔸 Long-term: no calculations in the box
                $info.html(this.longInfoHTML);
                $errorContainer.empty();
                $cta.html(`<button class="js-book-now w-full bg-basel-blue hover:bg-basel-blue-dark h-11 sm:h-10 text-white text-sm sm:text-base font-medium rounded-md">${t('send_inquiry','Send an Inquiry')}</button>`);
            } else {
                // DAILY: use only what the API provided
                const n       = Number(nights)      || 0;      // API only
                const baseTot = Number(totalsGross) || 0;      // API only
                if (n <= 0 || baseTot <= 0) {
                    $info.empty(); $errorContainer.empty();
                    $cta.html(`<button class="js-book-now w-full bg-basel-blue hover:bg-basel-blue-dark h-11 sm:h-10 text-white text-sm sm:text-base font-medium rounded-md">${t('book_now','Book Now')}</button>`);
                    this.updateMobileBar();
                    return;
                }
                const vatPercentNum = totalsGrossNet > 0 ? (vatAmount / totalsGrossNet) * 100 : 0;
                const vatPercentStr = (Math.round(vatPercentNum * 10) / 10).toFixed(1);

                const extras   = this.getExtrasTotal(n, this.guests); // extras as a separate row
                const guestsCount = Number(this.guests) || Number($('select[name="guests"]').val()) || 1; // кількість гостей
                const GUEST_TAX_CHF = 4.20; // фіксована ставка на особу за ніч
                const guestTax = Math.max(0, GUEST_TAX_CHF * guestsCount * n); // 4.20 * гості * ночі
                const grandTot = baseTot + extras + guestTax;

                $info.empty();
                $break.html(`
                  <div class="space-y-2 pt-4 js-breakdown">
                    <div class="flex justify-between">
                      <span>${this.currencyFormat(priceFromGrossNet, currency, locale)} × ${n} night${n>1?'s':''}</span>
                      <span>${this.currencyFormat(totalsGrossNet, currency, locale)}</span>
                    </div>
                    ${extras > 0 ? `
                    <div class="flex justify-between">
                      <span>${t('extra_services','Extra services')}</span>
                      <span>${this.currencyFormat(extras, currency, locale)}</span>
                    </div>` : ''}
                    ${vatAmount > 0 ? `
                    <div class="flex justify-between">
                      <span>${t('vat','VAT')} (${vatPercentStr}%)</span>
                      <span>${this.currencyFormat(vatAmount, currency, locale)}</span>
                    </div>` : ''}
                    ${guestTax > 0 ? `
                    <div class="flex justify-between">
                      <span>${t('guest_tax','Guest Tax (%s per person)').replace('%s', this.currencyFormat(GUEST_TAX_CHF,currency,locale))}</span>
                      <span>${this.currencyFormat(guestTax, currency, locale)}</span>
                    </div>` : ''}
                    <hr class="border-gray-200"/>
                    <div class="flex justify-between font-bold text-lg">
                      <span>${t('total','Total')}</span>
                      <span>${this.currencyFormat(grandTot, currency, locale)}</span>
                    </div>
                  </div>
                `);
                $cta.html(`<button class="js-book-now w-full bg-basel-blue hover:bg-basel-blue-dark h-11 sm:h-10 text-white text-sm sm:text-base font-medium rounded-md">${t('book_now','Book Now')}</button>`);

                this.updateMobileBar(); // the mobile summary will also update and take data only from the API
            }

            this.updateMobileBar();
        }

        // local render
        this.dates.checkIn  = $ci.val() || '';
        this.dates.checkOut = $co.val() || '';
        renderFromResh.call(this);

        // auto request at start if both effective dates exist
        if (hadUrlDates && this.apartmentId && !this._didInitialFetch) {
            const ci = this.dates.checkIn, co = this.getEffectiveCheckoutDate(this.dates.checkIn, this.dates.checkOut);
            const key = `${ci}|${co}|${this.guests}`;
            this._availKey = key;

            this.setAllCtasLoading(true); // 🔸 show loader in CTA

            let errorMsg = null;

            const similarIds = this.getSimilarApartmentIds();

            this.resetSimilarAvailabilityUI();

            this._availFetch = this.fetchUnitAvailability({ dateFrom: ci, dateTo: co, guests: this.guests, similarApartmentIds: similarIds, _key: key })
                .done((res) => {
                    const ok = !!(res && res.success && (res.data?.available ?? true));
                    if (!ok) errorMsg = res?.data?.message || t('apartment_is_not_available', 'The apartment is not available on these dates.');
                })
                .fail((jq) => {
                    errorMsg = jq?.responseJSON?.data?.message || t('apartment_is_not_available', 'The apartment is not available on these dates.');
                })
                .always(() => {
                    this._availFetch = null;
                    this.setAllCtasLoading(false); // remove loader first
                    renderFromResh();               // re-render once
                    if (errorMsg) {
                        this.showUnavailableUI($errorContainer, errorMsg);
                        this.renderSimilarAnchor($errorContainer);
                    }
                });

            this._didInitialFetch = true;
        }

        // ---- date changes ----
        const recalc = (changedField = '') => {
            if (this._suppressCheckoutChange && changedField === 'checkout') return;

            if (changedField === 'checkout' && this.hasUnsureCheckoutParam()) {
                const checkoutValue = $co.val() || '';

                if (this._preserveUnsureCheckoutOnce) {
                    this._preserveUnsureCheckoutOnce = false;
                } else if (checkoutValue) {
                    // Only manual selected departure date should disable unsure checkout mode.
                    this.removeUnsureCheckoutMode();
                } else {
                    // Programmatic clear from unsureCheckout=true should NOT remove the param.
                    this.syncUnsureCheckoutCheckboxes(true);
                    this.unsureCheckoutActive = true;
                }
            }

            if (changedField === 'checkin' && this.hasUnsureCheckoutParam()) {
                this.syncUnsureCheckoutCheckboxes(true);
                this.toggleUnsureCheckoutUI(true);
            }

            this.dates.checkIn  = $ci.val() || '';
            this.dates.checkOut = $co.val() || '';

            if (this.hasUnsureCheckoutParam()) {
                this.updateUrlParams({ dateFrom: this.dates.checkIn || null, dateTo: null });
            } else {
                this.updateUrlParams({ dateFrom: this.dates.checkIn || null, dateTo: this.dates.checkOut || null });
            }

            this.renderUnsureCheckoutInfobox();
            this.refreshSimilarLinks();

            this._availabilityKnown = false;
            this._isAvailable = null;
            this._unavailableMsg = '';
            this.clearAvailabilityUI($errorContainer);

            renderFromResh.call(this);

            this.updateMobileBar();

            const ci = this.dates.checkIn, co = this.getEffectiveCheckoutDate(this.dates.checkIn, this.dates.checkOut);
            const nights = this.computeNights(ci, co);
            if (!ci || !co || nights <= 0) return;      // only when both effective dates are valid

            const key = `${ci}|${co}|${this.guests}`;
            this._availKey = key;

            if (this._availFetch && this._availFetch.abort) this._availFetch.abort();

            this.setAllCtasLoading(true); // 🔸 show loader during check

            const similarIds = this.getSimilarApartmentIds();

            this.resetSimilarAvailabilityUI();

            this._availFetch = this.fetchUnitAvailability({ dateFrom: ci, dateTo: co, guests: this.guests, similarApartmentIds: similarIds, _key: key })
                .done((res) => {
                    if (this._availKey !== key) return;
                    const ok = !!(res && res.success && (res.data?.available ?? true));
                    renderFromResh.call(this);
                    if (!ok) {
                        this.showUnavailableUI($errorContainer, res?.data?.message || t('apartment_is_not_available', 'The apartment is not available on these dates.'));
                        this.renderSimilarAnchor($errorContainer);
                    }
                })
                .fail((jq) => {
                    if (this._availKey !== key) return;
                    const msg = jq?.responseJSON?.data?.message || t('apartment_is_not_available', 'The apartment is not available on these dates.');
                    this.showUnavailableUI($errorContainer, msg);
                    this.renderSimilarAnchor($errorContainer);
                })
                .always(() => {
                    if (this._availKey === key) this._availFetch = null;
                    this.setAllCtasLoading(false); // 🔸 remove loader
                });
        };

        $container.off('change.unsureCheckout')
            .on('change.unsureCheckout', '[data-role="unsure-checkout"]', () => {
                const checked = $container.find('[data-role="unsure-checkout"]').is(':checked');

                if (checked) {
                    this.applyUnsureCheckoutMode($ci.val() || '');
                } else {
                    this.removeUnsureCheckoutMode();
                }

                recalc(checked ? 'checkin' : '');
            });

        if ($ci[0]._flatpickr && $co[0]._flatpickr) {
            $ci[0]._flatpickr.config.onChange.push(() => recalc('checkin'));
            $co[0]._flatpickr.config.onChange.push(() => recalc('checkout'));
        } else {
            $ci.on('change', () => recalc('checkin'));
            $co.on('change', () => recalc('checkout'));
        }

    }

    updateUrlParams(patch = {}) {
        const url = new URL(window.location.href);
        const sp  = url.searchParams;

        // update only our keys
        const keys = ['dateFrom','dateTo','guests','extras','unsureCheckout'];
        keys.forEach(k => {
            if (k in patch) {
                const v = patch[k];
                if (v == null || v === '' || (k === 'guests' && String(v) === '0')) sp.delete(k);
                else sp.set(k, String(v));
            }
        });

        // remove legacy params if still present
        if ('dateFrom' in patch) sp.delete('checkin');
        if ('dateTo'   in patch) sp.delete('checkout');
        if ('guests'   in patch) sp.delete('maxGuests');

        const next = url.toString();
        if (next !== window.location.href) {
            history.replaceState(null, '', next);  // without cluttering history
        }
    }


    toggleService(button, id, price) {
        var pid = String(id);
        var p   = Number(price) || 0;
        var isSelected = Object.prototype.hasOwnProperty.call(this.addedExtraServices, pid);

        if (isSelected) {
            delete this.addedExtraServices[pid];
        } else {
            this.addedExtraServices[pid] = p;
        }

        // update button appearance
        this.updateServiceButton(button, !isSelected);

        // re-render the right price box (only it)
        this.refreshPricing();
    }

    updateServiceButton(button, active) {
        var $btn = $(button);
        if (active) {
            $btn.addClass('bg-primary text-white')
                .removeClass('border border-gray-300 text-gray-800')
                .html('<svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>'+t('added', 'Added'));
        } else {
            $btn.removeClass('bg-primary text-white')
                .addClass('border border-gray-300 text-gray-800')
                .text(t('add', 'Add'));
        }
    }

    refreshPricing() {
        const $root = $('#apartment-root');
        this.buildPricingBox($('#pricing-box'), this.apartmentData, $root, { autoFetchOnInit: false });
        this.updateMobileBar(); // ← also update the mobile bar
    }

    bindRepeats(root, ctx) {
        var self = this;
        var $root = root && root.jquery ? root : $(root);

        $root.find("[data-repeat]").each(function () {
            var $container = $(this);
            var path = $container.data("repeat");
            var arr = self.getNested(ctx, path);

            // Clear except <template>
            $container.children(":not(template)").remove();

            if (!$.isArray(arr) || !arr.length) {
                if (path === "nearbyAttractions") {
                    self.buildNearbyAttractions($container, [], $root);
                    return;
                }

                if ($container.is("[data-empty-hide]")) $container.prop("hidden", true);
                return;
            }
            $container.prop("hidden", false);

            switch (path) {
                case "features":
                    self.buildFeatures($container, arr, $root);
                    break;
                case "extras":
                    self.buildExtraServices($container, arr, $root);
                    break;
                case "similarUnits.units":
                    self.buildSimilarApartments($container, arr, $root);
                    break;
                case "nearbyAttractions":
                    self.buildNearbyAttractions($container, arr, $root);
                    break;
                case "images":
                    self.buildGallery($container, arr, $root, ctx);
                    break;
                default:
                    self.buildDefaultRepeat($container, arr, $root, ctx);
                    break;
            }
        });
    }

    renderApartment(rootSelector, data) {
        const $root = $(rootSelector);
        if (!$root.length) return;

        const ctx = { ...data, _computed: this.computeDerived(data) };
        this.bindSimple($root, ctx);
        this.bindRepeats($root, ctx);


        this.buildPricingBox($('#pricing-box'), ctx, $root);

        $root.removeClass('is-loading').attr('aria-busy', 'false');
        $root.find('.state--error').prop('hidden', true);
    }

    getApartmentData() {
        const apartment_id = this.apartmentId;
        if(!apartment_id) return false;

        const apartmentData = { apartment_id };

        return $.ajax({
            url: `${this.proxyUrl}?type=getApartmentInformation`,
            method: 'POST',
            contentType: 'application/json',
            dataType: 'json',
            data: JSON.stringify(apartmentData),
            success: (response) => {
                const data = (response && typeof response === 'object' && 'data' in response) ? response.data : response;
                this.apartmentData = data;
                this.hydrateExtrasFromUrl();
                this.renderApartment('#apartment-root', data);
                this._rvTrack(this.apartmentId);
            },
            error: (xhr) => {
                console.log(xhr.responseText);
            }
        });
    }



}

// init API service
const singleApartmentApi = new SingleApartmentApi();


// globally, as before
const selected = new Set();

// delegated click
$(document).on('click', '.js-extra-toggle', function (e) {
    const $btn = $(this);
    const id   = String($btn.data('serviceId') ?? $btn.attr('data-service-id'));
    let price  = Number($btn.data('servicePrice') ?? $btn.attr('data-service-price'));
    if (!Number.isFinite(price)) price = 0;                   // ← default price
    const freq = String($btn.data('serviceFrequency') ?? '');

    const isActive = Object.prototype.hasOwnProperty.call(singleApartmentApi.addedExtraServices, id);
    if (isActive) {
        delete singleApartmentApi.addedExtraServices[id];
    } else {
        singleApartmentApi.addedExtraServices[id] = { price, freq }; // save even when 0
    }

    const extrasIds = Object.keys(singleApartmentApi.addedExtraServices).sort();
    singleApartmentApi.updateUrlParams({ extras: extrasIds.length ? extrasIds.join(',') : null });

    $btn.toggleClass('bg-primary text-white', !isActive)
        .toggleClass('border border-gray-300 text-gray-800', isActive)
        .attr('aria-pressed', String(!isActive))
        .html(!isActive
            ? '<svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>' + t('added', 'Added')
            : t('add', 'Add'));

    singleApartmentApi.refreshPricing();
});

$(document).on('click', '#pricing-box .js-cta .js-book-now, #pricing-box-mobile .js-cta .js-book-now', function (e) {
    singleApartmentApi.onBookNowClick(e);
});

$(document).on('click', 'a.js-similar-link', function () {
    const base = $(this).data('baseHref') || $(this).attr('href');
    const finalHref = singleApartmentApi.addForwardParamsForLinks(base);
    $(this).attr('href', finalHref);
});

$(document).on('click', 'a.js-similar-anchor', function (e) {
    e.preventDefault();

    singleApartmentApi.scrollToSimilarSection();
});